import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Plus, Save, Eye, Trash2, Edit3, ChevronDown, ChevronRight, GripVertical } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Компонент для сортируемого вопроса
const SortableQuestion = ({ 
  question, 
  questionIndex, 
  categoryIndex,
  onEdit, 
  onDelete,
  forceUpdate 
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: `question-${categoryIndex}-${questionIndex}` });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="bg-white border border-gray-200 rounded-lg p-3">
      <div className="flex justify-between items-start">
        <div className="flex items-start space-x-3 flex-1">
          <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing mt-1">
            <GripVertical className="h-5 w-5 text-gray-400 hover:text-gray-600" />
          </div>
          <div className="flex-1">
            <h5 
              className="font-medium text-gray-900 mb-1 faq-question-header"
              dangerouslySetInnerHTML={{ __html: question.question }}
            />
            <p 
              className="text-sm text-gray-600 prose"
              dangerouslySetInnerHTML={{ __html: question.answer }}
            />
          </div>
        </div>
        <div className="flex items-center space-x-2 ml-2">
          <button
            onClick={() => onEdit()}
            className="text-blue-600 hover:text-blue-700"
            title="Редактировать вопрос"
          >
            <Edit3 className="h-4 w-4" />
          </button>
          <button
            onClick={() => onDelete()}
            className="text-red-600 hover:text-red-700"
            title="Удалить вопрос"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

// Компонент для сортируемой категории
const SortableCategory = ({ 
  category, 
  categoryIndex, 
  isExpanded, 
  onToggle, 
  onEdit, 
  onDelete, 
  onAddQuestion,
  onEditQuestion,
  onDeleteQuestion,
  onDragEndQuestion,
  forceUpdate 
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: `category-${categoryIndex}` });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  // Настройка сенсоров для drag and drop вопросов
  const questionSensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  return (
    <div ref={setNodeRef} style={style} className="border border-gray-200 rounded-lg">
      <div className="p-4 bg-gray-50 cursor-pointer flex justify-between items-center">
        <div className="flex items-center space-x-3 flex-1" onClick={onToggle}>
          <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing">
            <GripVertical className="h-5 w-5 text-gray-400 hover:text-gray-600" />
          </div>
          <div className="flex-1">
            <h3 
              className="font-semibold text-gray-900"
              dangerouslySetInnerHTML={{ __html: category.name }}
            />
            {category.description && (
              <p className="text-sm text-gray-600 mt-1">{category.description}</p>
            )}
            <p className="text-xs text-gray-500 mt-1">
              {category.questions.length} вопросов
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onEdit();
            }}
            className="text-blue-600 hover:text-blue-700"
            title="Редактировать категорию"
          >
            <Edit3 className="h-4 w-4" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            className="text-red-600 hover:text-red-700"
            title="Удалить категорию"
          >
            <Trash2 className="h-4 w-4" />
          </button>
          {isExpanded ? (
            <ChevronDown className="h-5 w-5 text-gray-400" />
          ) : (
            <ChevronRight className="h-5 w-5 text-gray-400" />
          )}
        </div>
      </div>

      {isExpanded && (
        <div className="p-4 border-t border-gray-200">
          <div className="flex justify-between items-center mb-3">
            <h4 className="font-medium text-gray-900">Вопросы</h4>
            <button
              onClick={onAddQuestion}
              className="text-orange-600 hover:text-orange-700 text-sm flex items-center space-x-1"
            >
              <Plus className="h-3 w-3" />
              <span>Добавить вопрос</span>
            </button>
          </div>

          {category.questions.length === 0 ? (
            <p className="text-gray-500 text-sm">В этой категории пока нет вопросов</p>
          ) : (
            <DndContext
              sensors={questionSensors}
              collisionDetection={closestCenter}
              onDragEnd={(event) => onDragEndQuestion(event, categoryIndex)}
            >
              <SortableContext
                items={category.questions.map((_, qIndex) => `question-${categoryIndex}-${qIndex}`)}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-3">
                  {category.questions.map((question, questionIndex) => (
                    <SortableQuestion
                      key={`${categoryIndex}-${questionIndex}-${question._id || questionIndex}-${forceUpdate}`}
                      question={question}
                      questionIndex={questionIndex}
                      categoryIndex={categoryIndex}
                      onEdit={() => onEditQuestion(questionIndex)}
                      onDelete={() => onDeleteQuestion(questionIndex)}
                      forceUpdate={forceUpdate}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </div>
      )}
    </div>
  );
};

const FAQEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [faq, setFaq] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showQuestionModal, setShowQuestionModal] = useState(false);
  const [showEditCategoryModal, setShowEditCategoryModal] = useState(false);
  const [showEditQuestionModal, setShowEditQuestionModal] = useState(false);
  const [selectedCategoryIndex, setSelectedCategoryIndex] = useState(null);
  const [selectedQuestionIndex, setSelectedQuestionIndex] = useState(null);
  const [editingCategory, setEditingCategory] = useState({ name: '', description: '' });
  const [editingQuestion, setEditingQuestion] = useState({ question: '', answer: '' });
  const [newCategory, setNewCategory] = useState({ name: '', description: '' });
  const [newQuestion, setNewQuestion] = useState({ question: '', answer: '' });
  
  // Состояния для форматирования текста
  const [selectedFont, setSelectedFont] = useState('');
  const [selectedSize, setSelectedSize] = useState('');
  const [showColorPalette, setShowColorPalette] = useState(false);
  const [showBgColorPalette, setShowBgColorPalette] = useState(false);
  const [selectedColor, setSelectedColor] = useState('#000000');
  const [selectedBgColor, setSelectedBgColor] = useState('#ffffff');
  const [activeField, setActiveField] = useState('question'); // 'question' или 'answer'
  const [forceUpdate, setForceUpdate] = useState(0);

  // Функция для извлечения чистого текста из HTML
  const extractTextFromHTML = (htmlString) => {
    if (!htmlString) return '';
    
    // Создаем временный DOM элемент
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlString;
    
    // Извлекаем только текстовое содержимое
    return tempDiv.textContent || tempDiv.innerText || '';
  };

  // Функция для валидации и обрезки HTML-текста
  const validateAndTrimHTML = (htmlString, maxLength = 100) => {
    if (!htmlString) return '';
    
    // Сначала извлекаем чистый текст для проверки длины
    const plainText = extractTextFromHTML(htmlString);
    
    // Если текст не превышает лимит, возвращаем оригинальный HTML
    if (plainText.length <= maxLength) {
      return htmlString;
    }
    
    // Если текст превышает лимит, обрезаем его и сохраняем форматирование
    const trimmedText = plainText.substring(0, maxLength);
    
    // Создаем временный DOM элемент для работы с HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlString;
    
    // Находим все стилизованные элементы
    const styledElements = tempDiv.querySelectorAll('*');
    let result = trimmedText;
    
    // Если есть стили, применяем их к обрезанному тексту
    if (styledElements.length > 0) {
      // Берем стили с первого элемента
      const firstElement = styledElements[0];
      const computedStyle = window.getComputedStyle(firstElement);
      
      // Создаем span с сохранением основных стилей
      const span = document.createElement('span');
      if (firstElement.style.color) span.style.color = firstElement.style.color;
      if (firstElement.style.fontFamily) span.style.fontFamily = firstElement.style.fontFamily;
      if (firstElement.style.fontSize) span.style.fontSize = firstElement.style.fontSize;
      if (firstElement.style.backgroundColor) span.style.backgroundColor = firstElement.style.backgroundColor;
      if (firstElement.style.fontWeight) span.style.fontWeight = firstElement.style.fontWeight;
      
      span.textContent = trimmedText;
      result = span.outerHTML;
    }
    
    return result;
  };

  // Состояния для настройки темы
  const [showThemeModal, setShowThemeModal] = useState(false);
  const [themeSettings, setThemeSettings] = useState({
    categoryBgColor: '#fef3c7',
    categoryHoverColor: '#fed7aa',
    questionBgColor: '#f9fafb',
    answerBgColor: '#ffffff',
    borderColor: '#f97316',
    borderWidth: '1px',
    contentBorderColor: '#e5e7eb',
    contentBorderWidth: '1px',
    primaryColor: '#ea580c',
    arrowColor: '#f97316',
    generalColor: '#ea580c'
  });

  // Настройка сенсоров для drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    fetchFaq();
  }, [id]);

  const fetchFaq = async () => {
    try {
      const response = await axios.get(`/api/faq/${id}`);
      console.log('📥 Загруженные данные FAQ:', response.data);
      console.log('📥 Категории:', response.data.categories);
      response.data.categories.forEach((category, catIndex) => {
        console.log(`📥 Категория ${catIndex}:`, category);
        category.questions.forEach((question, qIndex) => {
          console.log(`📥 Вопрос ${qIndex}:`, {
            question: question.question,
            answer: question.answer
          });
        });
      });
      setFaq(response.data);
      
      // Загружаем настройки темы, если они есть
      if (response.data.theme) {
        console.log('🎨 Загружаем тему:', response.data.theme);
        setThemeSettings({
          categoryBgColor: response.data.theme.categoryBgColor || '#fef3c7',
          categoryHoverColor: response.data.theme.categoryHoverColor || '#fed7aa',
          questionBgColor: response.data.theme.questionBgColor || '#f9fafb',
          answerBgColor: response.data.theme.answerBgColor || '#ffffff',
          borderColor: response.data.theme.borderColor || '#f97316',
          borderWidth: response.data.theme.borderWidth || '1px',
          contentBorderColor: response.data.theme.contentBorderColor || '#e5e7eb',
          contentBorderWidth: response.data.theme.contentBorderWidth || '1px',
          primaryColor: response.data.theme.primaryColor || '#ea580c',
          arrowColor: response.data.theme.arrowColor || '#f97316',
          generalColor: response.data.theme.generalColor || '#ea580c'
        });
      }
      
      // Разворачиваем все категории по умолчанию
      const categoryIndices = response.data.categories.map((_, index) => index);
      setExpandedCategories(new Set(categoryIndices));
    } catch (error) {
      console.error('Ошибка получения FAQ:', error);
      toast.error('Ошибка загрузки FAQ');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const saveFaq = async () => {
    setSaving(true);
    try {
      await axios.put(`/api/faq/${id}`, {
        title: faq.title,
        description: faq.description,
        isPublic: faq.isPublic,
        hideAnswers: faq.hideAnswers,
        theme: themeSettings
      });
      toast.success('FAQ сохранен!');
    } catch (error) {
      console.error('Ошибка сохранения FAQ:', error);
      toast.error('Ошибка сохранения FAQ');
    } finally {
      setSaving(false);
    }
  };

  const saveTheme = async () => {
    try {
      console.log('🎨 Сохраняем тему:', themeSettings);
      console.log('🎨 URL:', `/api/faq/${id}`);
      
      const response = await axios.put(`/api/faq/${id}`, {
        title: faq.title,
        description: faq.description,
        isPublic: faq.isPublic,
        hideAnswers: faq.hideAnswers,
        theme: themeSettings
      });
      
      console.log('🎨 Ответ сервера:', response.data);
      setShowThemeModal(false);
      toast.success('Тема сохранена!');
    } catch (error) {
      console.error('Ошибка сохранения темы:', error);
      console.error('Детали ошибки:', error.response?.data);
      toast.error('Ошибка сохранения темы: ' + (error.response?.data?.message || error.message));
    }
  };

  const addCategory = async (e) => {
    e.preventDefault();
    try {
      // Проверяем длину текста перед сохранением
      const plainText = extractTextFromHTML(newCategory.name);
      const wasTrimmed = plainText.length > 100;
      
      // Валидируем и обрезаем HTML-текст, сохраняя форматирование
      const categoryData = {
        ...newCategory,
        name: validateAndTrimHTML(newCategory.name, 100)
      };
      
      const response = await axios.post(`/api/admin/faq/${id}/categories`, categoryData);
      setFaq(prev => ({
        ...prev,
        categories: [...prev.categories, response.data.category]
      }));
      setNewCategory({ name: '', description: '' });
      setShowCategoryModal(false);
      
      if (wasTrimmed) {
        toast.success('Категория добавлена! (Текст был обрезан до 100 символов)');
      } else {
        toast.success('Категория добавлена!');
      }
    } catch (error) {
      console.error('Ошибка добавления категории:', error);
      toast.error('Ошибка добавления категории');
    }
  };

  const deleteCategory = async (categoryIndex) => {
    if (!window.confirm('Вы уверены, что хотите удалить эту категорию?')) {
      return;
    }

    try {
      await axios.delete(`/api/admin/faq/${id}/categories/${categoryIndex}`);
      setFaq(prev => ({
        ...prev,
        categories: prev.categories.filter((_, index) => index !== categoryIndex)
      }));
      toast.success('Категория удалена!');
    } catch (error) {
      console.error('Ошибка удаления категории:', error);
      toast.error('Ошибка удаления категории');
    }
  };

  const addQuestion = async (e) => {
    e.preventDefault();
    
    // Предотвращаем множественные вызовы
    if (addQuestion.isAdding) {
      console.log('🚫 Добавление уже в процессе, пропускаем...');
      return;
    }
    
    addQuestion.isAdding = true;
    
    try {
      const response = await axios.post(
        `/api/admin/faq/${id}/categories/${selectedCategoryIndex}/questions`,
        {
          question: cleanFontTags(newQuestion.question),
          answer: cleanFontTags(newQuestion.answer)
        }
      );
      setFaq(prev => ({
        ...prev,
        categories: prev.categories.map((cat, index) => 
          index === selectedCategoryIndex 
            ? { ...cat, questions: [...cat.questions, response.data.question] }
            : cat
        )
      }));
      setNewQuestion({ question: '', answer: '' });
      setShowQuestionModal(false);
      setSelectedCategoryIndex(null);
      toast.success('Вопрос добавлен!');
    } catch (error) {
      console.error('Ошибка добавления вопроса:', error);
      toast.error('Ошибка добавления вопроса');
    } finally {
      // Сбрасываем флаг в любом случае
      addQuestion.isAdding = false;
    }
  };

  const deleteQuestion = async (categoryIndex, questionIndex) => {
    if (!window.confirm('Вы уверены, что хотите удалить этот вопрос?')) {
      return;
    }

    try {
      await axios.delete(`/api/admin/faq/${id}/categories/${categoryIndex}/questions/${questionIndex}`);
      setFaq(prev => ({
        ...prev,
        categories: prev.categories.map((cat, index) => 
          index === categoryIndex 
            ? { ...cat, questions: cat.questions.filter((_, qIndex) => qIndex !== questionIndex) }
            : cat
        )
      }));
      toast.success('Вопрос удален!');
    } catch (error) {
      console.error('Ошибка удаления вопроса:', error);
      toast.error('Ошибка удаления вопроса');
    }
  };

  const editCategory = (categoryIndex) => {
    const category = faq.categories[categoryIndex];
    setEditingCategory({
      name: category.name, // Сохраняем HTML-форматирование для редактирования
      description: category.description || ''
    });
    setSelectedCategoryIndex(categoryIndex);
    setShowEditCategoryModal(true);
  };

  const updateCategory = async (e) => {
    e.preventDefault();
    try {
      // Проверяем длину текста перед сохранением
      const plainText = extractTextFromHTML(editingCategory.name);
      const wasTrimmed = plainText.length > 100;
      
      // Валидируем и обрезаем HTML-текст, сохраняя форматирование
      const categoryData = {
        ...editingCategory,
        name: validateAndTrimHTML(editingCategory.name, 100)
      };
      
      const response = await axios.put(
        `/api/admin/faq/${id}/categories/${selectedCategoryIndex}`,
        categoryData
      );
      setFaq(prev => ({
        ...prev,
        categories: prev.categories.map((cat, index) => 
          index === selectedCategoryIndex 
            ? { ...cat, ...response.data.category }
            : cat
        )
      }));
      setShowEditCategoryModal(false);
      setEditingCategory({ name: '', description: '' });
      setSelectedCategoryIndex(null);
      
      if (wasTrimmed) {
        toast.success('Категория обновлена! (Текст был обрезан до 100 символов)');
      } else {
        toast.success('Категория обновлена!');
      }
    } catch (error) {
      console.error('Ошибка обновления категории:', error);
      toast.error('Ошибка обновления категории');
    }
  };

  const editQuestion = (categoryIndex, questionIndex) => {
    const question = faq.categories[categoryIndex].questions[questionIndex];
    setEditingQuestion({
      question: question.question,
      answer: question.answer
    });
    setSelectedCategoryIndex(categoryIndex);
    setSelectedQuestionIndex(questionIndex);
    setShowEditQuestionModal(true);
    
    // Сбрасываем флаги инициализации для новых элементов
    setTimeout(() => {
      const questionEditor = document.querySelector('[contenteditable]');
      const answerEditor = document.querySelectorAll('[contenteditable]')[1];
      if (questionEditor) questionEditor.dataset.initialized = '';
      if (answerEditor) answerEditor.dataset.initialized = '';
    }, 0);
  };

  const updateQuestion = async (e) => {
    e.preventDefault();
    
    // Предотвращаем множественные вызовы
    if (updateQuestion.isUpdating) {
      console.log('🚫 Обновление уже в процессе, пропускаем...');
      return;
    }
    
    updateQuestion.isUpdating = true;
    
    try {
      console.log('💾 Отправляем данные на сервер:', {
        question: editingQuestion.question,
        answer: editingQuestion.answer
      });
      
      const response = await axios.put(
        `/api/admin/faq/${id}/categories/${selectedCategoryIndex}/questions/${selectedQuestionIndex}`,
        {
          question: editingQuestion.question,
          answer: editingQuestion.answer
        }
      );
      
      console.log('💾 Ответ сервера:', response.data);
      
      setFaq(prev => {
        const updatedFaq = {
          ...prev,
          categories: prev.categories.map((cat, index) => 
            index === selectedCategoryIndex 
              ? { 
                  ...cat, 
                  questions: cat.questions.map((q, qIndex) => 
                    qIndex === selectedQuestionIndex 
                      ? { ...q, ...response.data.question }
                      : q
                  )
                }
              : cat
          )
        };
        console.log('🔄 Обновленное состояние FAQ:', updatedFaq);
        return updatedFaq;
      });
      
      setShowEditQuestionModal(false);
      setEditingQuestion({ question: '', answer: '' });
      setSelectedCategoryIndex(null);
      setSelectedQuestionIndex(null);
      setForceUpdate(prev => prev + 1); // Принудительное обновление
      toast.success('Вопрос обновлен!');
    } catch (error) {
      console.error('Ошибка обновления вопроса:', error);
      toast.error('Ошибка обновления вопроса');
    } finally {
      // Сбрасываем флаг в любом случае
      updateQuestion.isUpdating = false;
    }
  };

  const toggleCategory = (index) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedCategories(newExpanded);
  };

  // Обработчик перетаскивания категорий
  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setFaq((prev) => {
        const oldIndex = prev.categories.findIndex((cat, idx) => `category-${idx}` === active.id);
        const newIndex = prev.categories.findIndex((cat, idx) => `category-${idx}` === over.id);

        const newCategories = arrayMove(prev.categories, oldIndex, newIndex);

        // Обновляем порядок категорий
        const updatedCategories = newCategories.map((cat, index) => ({
          ...cat,
          order: index
        }));

        // Сохраняем на сервере
        saveCategoryOrder(updatedCategories);

        return {
          ...prev,
          categories: updatedCategories
        };
      });
    }
  };

  // Сохранение порядка категорий на сервере
  const saveCategoryOrder = async (categories) => {
    try {
      await axios.put(`/api/faq/${id}/categories/reorder`, {
        categories
      });
      toast.success('Порядок категорий обновлен');
    } catch (error) {
      console.error('Ошибка сохранения порядка категорий:', error);
      toast.error('Ошибка сохранения порядка категорий');
    }
  };

  // Обработчик перетаскивания вопросов
  const handleQuestionDragEnd = async (event, categoryIndex) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setFaq((prev) => {
        const category = prev.categories[categoryIndex];
        
        // Извлекаем индексы из ID вопросов
        const oldIndex = parseInt(active.id.split('-')[2]);
        const newIndex = parseInt(over.id.split('-')[2]);

        const newQuestions = arrayMove(category.questions, oldIndex, newIndex);

        // Обновляем порядок вопросов
        const updatedQuestions = newQuestions.map((q, index) => ({
          ...q,
          order: index
        }));

        // Создаем новый массив категорий
        const newCategories = prev.categories.map((cat, idx) => {
          if (idx === categoryIndex) {
            return {
              ...cat,
              questions: updatedQuestions
            };
          }
          return cat;
        });

        // Сохраняем на сервере
        saveQuestionOrder(categoryIndex, updatedQuestions);

        return {
          ...prev,
          categories: newCategories
        };
      });
    }
  };

  // Сохранение порядка вопросов на сервере
  const saveQuestionOrder = async (categoryIndex, questions) => {
    try {
      await axios.put(`/api/faq/${id}/categories/${categoryIndex}/questions/reorder`, {
        questions
      });
      toast.success('Порядок вопросов обновлен');
    } catch (error) {
      console.error('Ошибка сохранения порядка вопросов:', error);
      toast.error('Ошибка сохранения порядка вопросов');
    }
  };

  // Функция для получения активного редактора
  const getActiveEditor = () => {
    const editors = document.querySelectorAll('[contenteditable]');
    
    if (showEditCategoryModal) {
      // В модальном окне редактирования категории
      return activeField === 'question' ? editors[editors.length - 2] : editors[editors.length - 1];
    } else if (showEditQuestionModal) {
      // В модальном окне редактирования вопроса - используем последние два элемента
      return activeField === 'question' ? editors[editors.length - 2] : editors[editors.length - 1];
    } else {
      // В обычном модальном окне добавления вопроса - используем первые два элемента
      return activeField === 'question' ? editors[0] : editors[1];
    }
  };

  // Функция для умной очистки HTML - убирает лишние теги и вложенности
  const cleanFontTags = (html) => {
    if (!html || html.trim() === '') return '';
    
    return html
      // Конвертируем font теги в span
      .replace(/<font[^>]*color="([^"]*)"[^>]*>/gi, '<span style="color: $1">')
      .replace(/<font[^>]*style="[^"]*background-color:\s*([^;"]*)[^"]*"[^>]*>/gi, '<span style="background-color: $1">')
      .replace(/<\/font>/gi, '</span>')
      .replace(/<font[^>]*>/gi, '')
      .replace(/<\/font>/gi, '')
      // Убираем лишние пробелы и символы
      .replace(/&nbsp;/gi, ' ')
      // Убираем пустые span теги
      .replace(/<span[^>]*><\/span>/gi, '')
      // Убираем span с прозрачным фоном без содержимого
      .replace(/<span style="background-color:\s*transparent[^"]*"><\/span>/gi, '')
      // Убираем span с прозрачным фоном и содержимым - оставляем только содержимое
      .replace(/<span style="background-color:\s*transparent[^"]*">([^<]*)<\/span>/gi, '$1')
      // Убираем вложенные span с одинаковыми стилями
      .replace(/<span([^>]*)><span\1>/gi, '<span$1>')
      .replace(/<\/span><\/span>/gi, '</span>')
      // Убираем span с цветом по умолчанию (черный)
      .replace(/<span style="color:\s*rgb\(0,\s*0,\s*0\)[^"]*">([^<]*)<\/span>/gi, '$1')
      .replace(/<span style="color:\s*#000000[^"]*">([^<]*)<\/span>/gi, '$1')
      .replace(/<span style="color:\s*black[^"]*">([^<]*)<\/span>/gi, '$1')
      // Убираем span с белым фоном по умолчанию
      .replace(/<span style="background-color:\s*rgb\(255,\s*255,\s*255\)[^"]*">([^<]*)<\/span>/gi, '$1')
      .replace(/<span style="background-color:\s*#ffffff[^"]*">([^<]*)<\/span>/gi, '$1')
      .replace(/<span style="background-color:\s*white[^"]*">([^<]*)<\/span>/gi, '$1')
      // Убираем лишние стили форматирования
      .replace(/<span style="font-weight:\s*normal[^"]*">([^<]*)<\/span>/gi, '$1')
      .replace(/<span style="font-style:\s*normal[^"]*">([^<]*)<\/span>/gi, '$1')
      .replace(/<span style="text-decoration:\s*none[^"]*">([^<]*)<\/span>/gi, '$1')
      // Убираем span с только лишними стилями
      .replace(/<span style="font-weight:\s*normal;\s*">([^<]*)<\/span>/gi, '$1')
      .replace(/<span style="font-style:\s*normal;\s*">([^<]*)<\/span>/gi, '$1')
      .replace(/<span style="text-decoration:\s*none;\s*">([^<]*)<\/span>/gi, '$1')
      // Убираем пустые стили
      .replace(/<span style=";\s*">([^<]*)<\/span>/gi, '$1')
      .replace(/<span style="">([^<]*)<\/span>/gi, '$1')
      // Исправляем разорванные теги - убираем лишние закрывающие теги
      .replace(/<\/span>(?!<)/g, (match, offset, string) => {
        // Считаем количество открывающих и закрывающих span тегов до этой позиции
        const beforeMatch = string.substring(0, offset);
        const openTags = (beforeMatch.match(/<span[^>]*>/g) || []).length;
        const closeTags = (beforeMatch.match(/<\/span>/g) || []).length;
        // Если закрывающих тегов больше чем открывающих, убираем этот тег
        return closeTags >= openTags ? '' : match;
      })
      // Убираем вложенные font теги
      .replace(/<font[^>]*><font[^>]*>([^<]*)<\/font><\/font>/gi, '<font>$1</font>')
      // Убираем лишние закрывающие теги в конце
      .replace(/<\/span>$/g, '')
      .replace(/<\/span>\s*$/g, '')
      .replace(/<\/font>$/g, '')
      .replace(/<\/font>\s*$/g, '');
  };

  // Функция для применения форматирования к активному полю
  const applyFormatting = (command, value = false) => {
    const editor = getActiveEditor();
    if (editor) {
      editor.focus();
      
      // Очищаем предыдущие стили перед применением новых
      if (command === 'fontName' || command === 'fontSize' || command === 'foreColor') {
    const selection = window.getSelection();
        if (selection.rangeCount > 0) {
    const range = selection.getRangeAt(0);
    
          // Если есть выделение - очищаем стили в выделенном тексте
        if (!range.collapsed) {
            const selectedText = range.extractContents();
            const textNode = document.createTextNode(selectedText.textContent);
      range.insertNode(textNode);
            range.selectNode(textNode);
      selection.removeAllRanges();
      selection.addRange(range);
    }
        }
      }
      
      // Получаем текущее выделение
      const selection = window.getSelection();
      
      // Для команд форматирования без выделения:
      // - bold, italic, underline - выделяем весь текст
      // - цвета, шрифты, размеры - НЕ выделяем, применяем к текущей позиции курсора
      if ((command === 'bold' || command === 'italic' || command === 'underline') &&
          (selection.rangeCount === 0 || selection.isCollapsed)) {
        const range = document.createRange();
        range.selectNodeContents(editor);
        selection.removeAllRanges();
        selection.addRange(range);
      }
      
      // Отладка для команд списков
      if (command === 'insertUnorderedList' || command === 'insertOrderedList') {
        console.log('📝 Создание списка:', command);
        console.log('📝 Активный редактор:', editor);
        console.log('📝 Выделение:', selection.toString());
      }
      
      // Для цветов используем современный подход с span
      if (command === 'foreColor') {
        const range = selection.getRangeAt(0);
        if (!range.collapsed) {
          // Если есть выделение - применяем к выделенному тексту
          const span = document.createElement('span');
          span.style.color = value;
          try {
            range.surroundContents(span);
          } catch (e) {
            range.insertNode(span);
          }
        } else {
          // Если нет выделения - проверяем, есть ли уже span с цветом в позиции курсора
          const container = range.commonAncestorContainer;
          let existingSpan = null;
          
          // Ищем существующий span с цветом
          if (container.nodeType === Node.TEXT_NODE) {
            const parent = container.parentNode;
            if (parent && parent.tagName === 'SPAN' && parent.style.color) {
              existingSpan = parent;
            }
          } else if (container.tagName === 'SPAN' && container.style.color) {
            existingSpan = container;
          }
          
          if (existingSpan) {
            // Обновляем цвет существующего span
            existingSpan.style.color = value;
            // Перемещаем курсор в конец span
            range.setStartAfter(existingSpan);
            range.collapse(true);
            selection.removeAllRanges();
            selection.addRange(range);
          } else {
            // Создаем новый span только если его нет
            const span = document.createElement('span');
            span.style.color = value;
            span.innerHTML = '&nbsp;';
            range.insertNode(span);
            // Перемещаем курсор внутрь span
            range.setStartAfter(span);
            range.collapse(true);
            selection.removeAllRanges();
            selection.addRange(range);
          }
        }
      } else if (command === 'fontName') {
        const range = selection.getRangeAt(0);
        if (!range.collapsed) {
          // Если есть выделение - применяем к выделенному тексту
          const span = document.createElement('span');
          span.style.fontFamily = value;
          try {
            range.surroundContents(span);
          } catch (e) {
            range.insertNode(span);
          }
        } else {
          // Если нет выделения - создаем span в позиции курсора для будущего ввода
          const span = document.createElement('span');
          span.style.fontFamily = value;
          span.innerHTML = '&nbsp;'; // Невидимый пробел для span
          range.insertNode(span);
          // Перемещаем курсор внутрь span
          range.setStartAfter(span);
          range.collapse(true);
          selection.removeAllRanges();
          selection.addRange(range);
        }
      } else if (command === 'backColor') {
        const range = selection.getRangeAt(0);
        if (!range.collapsed) {
          // Если есть выделение - применяем к выделенному тексту
          if (value === 'transparent') {
            // Для прозрачного фона - удаляем все span с background-color
            const selectedHTML = range.cloneContents();
            const walker = document.createTreeWalker(
              selectedHTML,
              NodeFilter.SHOW_ELEMENT,
              null,
              false
            );
            
            const elementsToClean = [];
            let node;
            while (node = walker.nextNode()) {
              if (node.style && node.style.backgroundColor) {
                elementsToClean.push(node);
              }
            }
            
            // Заменяем содержимое без span с фоном
            const tempDiv = document.createElement('div');
            tempDiv.appendChild(selectedHTML.cloneNode(true));
            
            elementsToClean.forEach(element => {
              const parent = element.parentNode;
              while (element.firstChild) {
                parent.insertBefore(element.firstChild, element);
              }
              parent.removeChild(element);
            });
            
            range.deleteContents();
            range.insertNode(tempDiv);
          } else {
            const span = document.createElement('span');
            span.style.backgroundColor = value;
            try {
              range.surroundContents(span);
            } catch (e) {
              range.insertNode(span);
            }
          }
        } else {
          // Если нет выделения - создаем span в позиции курсора для будущего ввода
          if (value === 'transparent') {
            // Для прозрачного фона ничего не делаем
            return;
          } else {
            const span = document.createElement('span');
            span.style.backgroundColor = value;
            span.innerHTML = '&nbsp;'; // Невидимый пробел для span
            range.insertNode(span);
            // Перемещаем курсор внутрь span
            range.setStartAfter(span);
            range.collapse(true);
            selection.removeAllRanges();
            selection.addRange(range);
          }
        }
      } else {
        // Для остальных команд используем стандартный execCommand
        console.log('📝 Выполняем execCommand:', command, value);
        const result = document.execCommand(command, false, value);
        console.log('📝 Результат execCommand:', result);
        
        // Отладка для списков - проверяем что создалось
        if (command === 'insertUnorderedList' || command === 'insertOrderedList') {
          console.log('📝 HTML после создания списка:', editor.innerHTML);
        }
      }
      
      // Обновляем состояние после применения форматирования
      if (showEditCategoryModal) {
        // Если мы в модальном окне редактирования категории
        if (activeField === 'question') {
          setEditingCategory({ ...editingCategory, name: editor.innerHTML });
        } else {
          setEditingCategory({ ...editingCategory, description: editor.innerHTML });
        }
      } else if (showEditQuestionModal) {
        // Если мы в модальном окне редактирования вопроса
        if (activeField === 'question') {
          setEditingQuestion({ ...editingQuestion, question: editor.innerHTML });
        } else {
          setEditingQuestion({ ...editingQuestion, answer: editor.innerHTML });
        }
      } else {
        // Если мы в обычном модальном окне добавления вопроса
        if (activeField === 'question') {
          setNewQuestion({ ...newQuestion, question: editor.innerHTML });
        } else {
          setNewQuestion({ ...newQuestion, answer: editor.innerHTML });
        }
      }
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-orange-600"></div>
      </div>
    );
  }

  if (!faq) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-gray-900">FAQ не найден</h2>
        <button
          onClick={() => navigate('/dashboard')}
          className="mt-4 text-orange-600 hover:text-orange-700"
        >
          Вернуться к дашборду
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Редактирование FAQ</h1>
          <p className="text-gray-600 mt-1">{faq.title}</p>
        </div>
        <div className="flex space-x-3">
          <a
            href={`/faq/${faq.slug}`}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-gray-100 text-gray-700 px-3 py-1.5 rounded-md hover:bg-gray-200 transition-colors flex items-center space-x-2 text-sm"
          >
            <Eye className="h-3 w-3" />
            <span>Просмотр</span>
          </a>
          <button
            onClick={() => setShowThemeModal(true)}
            className="bg-purple-600 text-white px-3 py-1.5 rounded-md hover:bg-purple-700 transition-colors flex items-center space-x-2 text-sm"
          >
            <span>🎨</span>
            <span>Тема</span>
          </button>
          <button
            onClick={saveFaq}
            disabled={saving}
            className="bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 transition-colors flex items-center space-x-2 disabled:opacity-50"
          >
            <Save className="h-4 w-4" />
            <span>{saving ? 'Сохранение...' : 'Сохранить'}</span>
          </button>
        </div>
      </div>

      {/* FAQ Settings */}
      <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4">Настройки FAQ</h2>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Название
            </label>
            <input
              type="text"
              value={faq.title}
              onChange={(e) => setFaq({ ...faq, title: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Статус
            </label>
            <select
              value={faq.isPublic ? 'public' : 'private'}
              onChange={(e) => setFaq({ ...faq, isPublic: e.target.value === 'public' })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
            >
              <option value="public">Публичный</option>
              <option value="private">Приватный</option>
            </select>
          </div>
        </div>
        <div className="mt-4">
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={faq.hideAnswers || false}
              onChange={(e) => setFaq({ ...faq, hideAnswers: e.target.checked })}
              className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
            />
            <span className="text-sm font-medium text-gray-700">
              Скрывать ответы (показывать только 100 символов)
            </span>
          </label>
          <p className="text-xs text-gray-500 mt-1 ml-6">
            Если включено, ответы будут скрыты, и пользователи смогут раскрывать их кликом
          </p>
        </div>
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Описание
          </label>
          <textarea
            value={faq.description || ''}
            onChange={(e) => setFaq({ ...faq, description: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
            rows="3"
          />
        </div>
      </div>

      {/* Categories */}
      <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Категории и вопросы</h2>
          <button
            onClick={() => setShowCategoryModal(true)}
            className="bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 transition-colors flex items-center space-x-2"
          >
            <Plus className="h-4 w-4" />
            <span>Добавить категорию</span>
          </button>
        </div>

        {faq.categories.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-500 mb-4">У вас пока нет категорий</p>
            <button
              onClick={() => setShowCategoryModal(true)}
              className="text-orange-600 hover:text-orange-700"
            >
              Создать первую категорию
            </button>
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={faq.categories.map((_, idx) => `category-${idx}`)}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-4">
                {faq.categories.map((category, categoryIndex) => (
                  <SortableCategory
                    key={`category-${categoryIndex}`}
                    category={category}
                    categoryIndex={categoryIndex}
                    isExpanded={expandedCategories.has(categoryIndex)}
                    onToggle={() => toggleCategory(categoryIndex)}
                    onEdit={() => editCategory(categoryIndex)}
                    onDelete={() => deleteCategory(categoryIndex)}
                    onAddQuestion={() => {
                      setSelectedCategoryIndex(categoryIndex);
                      setShowQuestionModal(true);
                    }}
                    onEditQuestion={(questionIndex) => editQuestion(categoryIndex, questionIndex)}
                    onDeleteQuestion={(questionIndex) => deleteQuestion(categoryIndex, questionIndex)}
                    onDragEndQuestion={handleQuestionDragEnd}
                    forceUpdate={forceUpdate}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </div>

      {/* Category Modal */}
      {showCategoryModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowCategoryModal(false);
            }
          }}
        >
          <div 
            className="bg-white rounded-lg p-6 w-full max-w-md mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-xl font-semibold mb-4">Добавить категорию</h2>
            <form onSubmit={addCategory}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Название категории
                </label>
                <input
                  type="text"
                  value={newCategory.name}
                  onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="Введите название категории"
                  required
                />
              </div>
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Описание (необязательно)
                </label>
                <textarea
                  value={newCategory.description}
                  onChange={(e) => setNewCategory({ ...newCategory, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  rows="3"
                  placeholder="Краткое описание категории"
                />
              </div>
              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={() => setShowCategoryModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Отмена
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
                >
                  Добавить
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Question Modal */}
      {showQuestionModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowQuestionModal(false);
              setSelectedCategoryIndex(null);
            }
          }}
        >
          <div 
            className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-xl font-semibold mb-4">Добавить вопрос</h2>
            <form onSubmit={addQuestion}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Вопрос
                </label>
                
                {/* Панель инструментов для вопроса */}
                <div className="border border-gray-300 rounded-lg">
                  <div className="border-b border-gray-300 p-2 bg-gray-50 rounded-t-lg">
                    <div className="flex flex-wrap gap-2 items-center">
                      <select 
                        className="px-2 py-1 border border-gray-300 rounded text-sm"
                        value={selectedFont}
                        onChange={(e) => {
                          setSelectedFont(e.target.value);
                          if (e.target.value) {
                            applyFormatting('fontName', e.target.value);
                          }
                        }}
                      >
                        <option value="">Шрифт</option>
                        <option value="Arial, sans-serif">Arial</option>
                        <option value="Helvetica, sans-serif">Helvetica</option>
                        <option value="Times New Roman, serif">Times New Roman</option>
                        <option value="Georgia, serif">Georgia</option>
                        <option value="Verdana, sans-serif">Verdana</option>
                        <option value="Courier New, monospace">Courier New</option>
                        <option value="Roboto, sans-serif">Roboto</option>
                        <option value="Open Sans, sans-serif">Open Sans</option>
                        <option value="PT Sans, sans-serif">PT Sans</option>
                        <option value="PT Serif, serif">PT Serif</option>
                        <option value="Lora, serif">Lora</option>
                        <option value="Playfair Display, serif">Playfair Display</option>
                        <option value="Montserrat, sans-serif">Montserrat</option>
                        <option value="Raleway, sans-serif">Raleway</option>
                        <option value="Ubuntu, sans-serif">Ubuntu</option>
                      </select>
                      
                      <select 
                        className="px-2 py-1 border border-gray-300 rounded text-sm"
                        value={selectedSize}
                        onChange={(e) => {
                          setSelectedSize(e.target.value);
                          if (e.target.value) {
                            const editor = getActiveEditor();
                            if (editor) {
                              editor.focus();
                              
                              const selection = window.getSelection();
                              const range = selection.getRangeAt(0);
                              
                              if (!range.collapsed) {
                                // Если есть выделение - применяем к выделенному тексту
                                const span = document.createElement('span');
                                span.style.fontSize = e.target.value;
                                try {
                                  range.surroundContents(span);
                                } catch (e) {
                                  range.insertNode(span);
                                }
                              } else {
                                // Если нет выделения - выделяем слово под курсором или создаем выделение для будущего ввода
                                const wordRange = document.createRange();
                                
                                // Пытаемся выделить слово под курсором
                                try {
                                  wordRange.selectNodeContents(editor);
                                  const text = editor.textContent || editor.innerText || '';
                                  const cursorPosition = range.startOffset;
                                  
                                  // Ищем границы слова
                                  let start = cursorPosition;
                                  let end = cursorPosition;
                                  
                                  // Ищем начало слова
                                  while (start > 0 && /\S/.test(text[start - 1])) {
                                    start--;
                                  }
                                  
                                  // Ищем конец слова
                                  while (end < text.length && /\S/.test(text[end])) {
                                    end++;
                                  }
                                  
                                  // Если нашли слово - выделяем его
                                  if (start < end) {
                                    wordRange.setStart(editor.firstChild || editor, start);
                                    wordRange.setEnd(editor.firstChild || editor, end);
                                  } else {
                                    // Если слова нет - создаем пустое выделение для будущего ввода
                                    wordRange.setStart(range.startContainer, range.startOffset);
                                    wordRange.setEnd(range.startContainer, range.startOffset);
                                  }
                                } catch (error) {
                                  // Если не удалось выделить слово - создаем пустое выделение
                                  wordRange.setStart(range.startContainer, range.startOffset);
                                  wordRange.setEnd(range.startContainer, range.startOffset);
                                }
                                
                                // Применяем размер к выделению
                                const span = document.createElement('span');
                                span.style.fontSize = e.target.value;
                                span.setAttribute('data-font-size', e.target.value);
                                
                                if (wordRange.collapsed) {
                                  // Если выделение пустое - добавляем невидимый символ
                                  span.innerHTML = '&#8203;';
                                  wordRange.insertNode(span);
                                  range.setStart(span, 1);
                                } else {
                                  // Если есть выделение - оборачиваем его
                                  try {
                                    wordRange.surroundContents(span);
                                  } catch (e) {
                                    wordRange.insertNode(span);
                                  }
                                  range.setStartAfter(span);
                                }
                                
                                range.collapse(true);
                                selection.removeAllRanges();
                                selection.addRange(range);
                              }
                              
                              if (activeField === 'question') {
                                setNewQuestion({ ...newQuestion, question: editor.innerHTML });
                              } else {
                                setNewQuestion({ ...newQuestion, answer: editor.innerHTML });
                              }
                            }
                          }
                        }}
                      >
                        <option value="">Размер</option>
                        <option value="12px">12px</option>
                        <option value="14px">14px</option>
                        <option value="16px">16px</option>
                        <option value="18px">18px</option>
                        <option value="20px">20px</option>
                        <option value="24px">24px</option>
                        <option value="28px">28px</option>
                        <option value="32px">32px</option>
                        <option value="36px">36px</option>
                      </select>
                      
                      <button 
                        type="button"
                        className="px-2 py-1 border border-gray-300 rounded text-sm hover:bg-gray-100"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          applyFormatting('bold');
                        }}
                        title="Жирный"
                      >
                        B
                      </button>
                      
                      <button 
                        type="button"
                        className="px-2 py-1 border border-gray-300 rounded text-sm hover:bg-gray-100"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          applyFormatting('italic');
                        }}
                        title="Курсив"
                      >
                        I
                      </button>
                      
                      <button 
                        type="button"
                        className="px-2 py-1 border border-gray-300 rounded text-sm hover:bg-gray-100"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          applyFormatting('underline');
                        }}
                        title="Подчеркивание"
                      >
                        U
                      </button>
                      
                      <button 
                        type="button"
                        className="px-2 py-1 border border-gray-300 rounded text-sm hover:bg-gray-100"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          applyFormatting('insertUnorderedList');
                        }}
                        title="Маркированный список"
                      >
                        •
                      </button>
                      
                      <button 
                        type="button"
                        className="px-2 py-1 border border-gray-300 rounded text-sm hover:bg-gray-100"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          applyFormatting('insertOrderedList');
                        }}
                        title="Нумерованный список"
                      >
                        1.
                      </button>
                      
                      {/* Кнопка цвета текста */}
                      <div className="relative color-palette-container">
                        <button 
                          type="button"
                          className="w-8 h-8 border border-gray-300 rounded text-sm hover:bg-gray-100 flex items-center justify-center"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setShowColorPalette(!showColorPalette);
                          }}
                          title={`Цвет текста: ${selectedColor}`}
                        >
                          <span 
                            className="text-lg font-bold"
                            style={{ color: selectedColor }}
                          >
                            A
                          </span>
                        </button>
                        
                        {/* Выпадающая палитра цветов */}
                        {showColorPalette && (
                          <div className="absolute top-full left-0 mt-1 p-4 bg-white border border-gray-300 rounded-lg shadow-lg z-10 w-48">
                            <div className="grid grid-cols-6 gap-1.5 mb-3">
                              <button 
                                type="button"
                                className="w-6 h-6 rounded border border-gray-300 bg-black hover:scale-110 transition-transform"
                                onClick={() => {
                                  applyFormatting('foreColor', '#000000');
                                  setSelectedColor('#000000');
                                  setShowColorPalette(false);
                                }}
                                title="Черный"
                              ></button>
                              <button 
                                type="button"
                                className="w-6 h-6 rounded border border-gray-300 bg-white hover:scale-110 transition-transform"
                                onClick={(e) => {
                                  applyFormatting('foreColor', '#ffffff');
                                  setSelectedColor('#ffffff');
                                  setShowColorPalette(false);
                                }}
                                title="Белый"
                              ></button>
                              <button 
                                type="button"
                                className="w-6 h-6 rounded border border-gray-300 bg-red-500 hover:scale-110 transition-transform"
                                onClick={(e) => {
                                  applyFormatting('foreColor', '#ef4444');
                                  setSelectedColor('#ef4444');
                                  setShowColorPalette(false);
                                }}
                                title="Красный"
                              ></button>
                              <button 
                                type="button"
                                className="w-6 h-6 rounded border border-gray-300 bg-orange-500 hover:scale-110 transition-transform"
                                onClick={(e) => {
                                  applyFormatting('foreColor', '#f97316');
                                  setSelectedColor('#f97316');
                                  setShowColorPalette(false);
                                }}
                                title="Оранжевый"
                              ></button>
                              <button 
                                type="button"
                                className="w-6 h-6 rounded border border-gray-300 bg-yellow-500 hover:scale-110 transition-transform"
                                onClick={(e) => {
                                  applyFormatting('foreColor', '#eab308');
                                  setSelectedColor('#eab308');
                                  setShowColorPalette(false);
                                }}
                                title="Желтый"
                              ></button>
                              <button 
                                type="button"
                                className="w-6 h-6 rounded border border-gray-300 bg-green-500 hover:scale-110 transition-transform"
                                onClick={(e) => {
                                  applyFormatting('foreColor', '#22c55e');
                                  setSelectedColor('#22c55e');
                                  setShowColorPalette(false);
                                }}
                                title="Зеленый"
                              ></button>
                              <button 
                                type="button"
                                className="w-6 h-6 rounded border border-gray-300 bg-blue-500 hover:scale-110 transition-transform"
                                onClick={(e) => {
                                  applyFormatting('foreColor', '#3b82f6');
                                  setSelectedColor('#3b82f6');
                                  setShowColorPalette(false);
                                }}
                                title="Синий"
                              ></button>
                              <button 
                                type="button"
                                className="w-6 h-6 rounded border border-gray-300 bg-purple-500 hover:scale-110 transition-transform"
                                onClick={(e) => {
                                  applyFormatting('foreColor', '#a855f7');
                                  setSelectedColor('#a855f7');
                                  setShowColorPalette(false);
                                }}
                                title="Фиолетовый"
                              ></button>
                              <button 
                                type="button"
                                className="w-6 h-6 rounded border border-gray-300 bg-pink-500 hover:scale-110 transition-transform"
                                onClick={(e) => {
                                  applyFormatting('foreColor', '#ec4899');
                                  setSelectedColor('#ec4899');
                                  setShowColorPalette(false);
                                }}
                                title="Розовый"
                              ></button>
                              <button 
                                type="button"
                                className="w-6 h-6 rounded border border-gray-300 bg-gray-500 hover:scale-110 transition-transform"
                                onClick={(e) => {
                                  applyFormatting('foreColor', '#6b7280');
                                  setSelectedColor('#6b7280');
                                  setShowColorPalette(false);
                                }}
                                title="Серый"
                              ></button>
                              <button 
                                type="button"
                                className="w-6 h-6 rounded border border-gray-300 bg-indigo-500 hover:scale-110 transition-transform"
                                onClick={(e) => {
                                  applyFormatting('foreColor', '#6366f1');
                                  setSelectedColor('#6366f1');
                                  setShowColorPalette(false);
                                }}
                                title="Индиго"
                              ></button>
                              <button 
                                type="button"
                                className="w-6 h-6 rounded border border-gray-300 bg-teal-500 hover:scale-110 transition-transform"
                                onClick={(e) => {
                                  applyFormatting('foreColor', '#14b8a6');
                                  setSelectedColor('#14b8a6');
                                  setShowColorPalette(false);
                                }}
                                title="Бирюзовый"
                              ></button>
                            </div>
                            
                            {/* Кастомный цвет */}
                            <div className="border-t border-gray-300 pt-2">
                              <label className="text-xs text-gray-600 mb-1 block">Кастомный цвет:</label>
                <input
                                type="color"
                                className="w-full h-8 rounded border border-gray-300 cursor-pointer"
                                onChange={(e) => {
                                  applyFormatting('foreColor', e.target.value);
                                  setSelectedColor(e.target.value);
                                  setShowColorPalette(false);
                                }}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                      
                      {/* Кнопка цвета фона */}
                      <div className="relative bg-color-palette-container">
                        <button 
                          type="button"
                          className="w-8 h-8 border border-gray-300 rounded text-sm hover:bg-gray-100 flex items-center justify-center relative"
                          style={{ backgroundColor: selectedBgColor === 'transparent' ? 'white' : selectedBgColor }}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setShowBgColorPalette(!showBgColorPalette);
                          }}
                          title={`Цвет фона: ${selectedBgColor}`}
                        >
                          {selectedBgColor === 'transparent' && (
                            <div className="absolute inset-0 bg-gradient-to-br from-red-200 to-blue-200 opacity-50 rounded"></div>
                          )}
                          <span 
                            className="text-lg font-bold relative z-10"
                            style={{ 
                              color: selectedBgColor === '#ffffff' || selectedBgColor === '#ffff00' || selectedBgColor === 'transparent' ? '#000000' : '#ffffff'
                            }}
                          >
                            A
                          </span>
                        </button>
                        
                        {/* Выпадающая палитра цветов фона */}
                        {showBgColorPalette && (
                          <div className="absolute top-full left-0 mt-1 p-4 bg-white border border-gray-300 rounded-lg shadow-lg z-10 w-48">
                            <div className="grid grid-cols-6 gap-1.5 mb-3">
                              <button 
                                type="button"
                                className="w-6 h-6 rounded border border-gray-300 bg-transparent hover:scale-110 transition-transform relative"
                                onClick={(e) => {
                                  applyFormatting('backColor', 'transparent');
                                  setSelectedBgColor('transparent');
                                  setShowBgColorPalette(false);
                                }}
                                title="Прозрачный фон"
                              >
                                <div className="absolute inset-0 bg-gradient-to-br from-red-200 to-blue-200 opacity-50 rounded"></div>
                                <div className="absolute inset-1 bg-white rounded-sm"></div>
                              </button>
                              
                              <button 
                                type="button"
                                className="w-6 h-6 rounded border border-gray-300 bg-black hover:scale-110 transition-transform"
                                onClick={(e) => {
                                  applyFormatting('backColor', '#000000');
                                  setSelectedBgColor('#000000');
                                  setShowBgColorPalette(false);
                                }}
                                title="Черный фон"
                              ></button>
                              <button 
                                type="button"
                                className="w-6 h-6 rounded border border-gray-300 bg-white hover:scale-110 transition-transform"
                                onClick={(e) => {
                                  applyFormatting('backColor', '#ffffff');
                                  setSelectedBgColor('#ffffff');
                                  setShowBgColorPalette(false);
                                }}
                                title="Белый фон"
                              ></button>
                              <button 
                                type="button"
                                className="w-6 h-6 rounded border border-gray-300 bg-red-500 hover:scale-110 transition-transform"
                                onClick={(e) => {
                                  applyFormatting('backColor', '#ef4444');
                                  setSelectedBgColor('#ef4444');
                                  setShowBgColorPalette(false);
                                }}
                                title="Красный фон"
                              ></button>
                              <button 
                                type="button"
                                className="w-6 h-6 rounded border border-gray-300 bg-orange-500 hover:scale-110 transition-transform"
                                onClick={(e) => {
                                  applyFormatting('backColor', '#f97316');
                                  setSelectedBgColor('#f97316');
                                  setShowBgColorPalette(false);
                                }}
                                title="Оранжевый фон"
                              ></button>
                              <button 
                                type="button"
                                className="w-6 h-6 rounded border border-gray-300 bg-yellow-500 hover:scale-110 transition-transform"
                                onClick={(e) => {
                                  applyFormatting('backColor', '#eab308');
                                  setSelectedBgColor('#eab308');
                                  setShowBgColorPalette(false);
                                }}
                                title="Желтый фон"
                              ></button>
                              <button 
                                type="button"
                                className="w-6 h-6 rounded border border-gray-300 bg-green-500 hover:scale-110 transition-transform"
                                onClick={(e) => {
                                  applyFormatting('backColor', '#22c55e');
                                  setSelectedBgColor('#22c55e');
                                  setShowBgColorPalette(false);
                                }}
                                title="Зеленый фон"
                              ></button>
                              <button 
                                type="button"
                                className="w-6 h-6 rounded border border-gray-300 bg-blue-500 hover:scale-110 transition-transform"
                                onClick={(e) => {
                                  applyFormatting('backColor', '#3b82f6');
                                  setSelectedBgColor('#3b82f6');
                                  setShowBgColorPalette(false);
                                }}
                                title="Синий фон"
                              ></button>
                              <button 
                                type="button"
                                className="w-6 h-6 rounded border border-gray-300 bg-purple-500 hover:scale-110 transition-transform"
                                onClick={(e) => {
                                  applyFormatting('backColor', '#a855f7');
                                  setSelectedBgColor('#a855f7');
                                  setShowBgColorPalette(false);
                                }}
                                title="Фиолетовый фон"
                              ></button>
                              <button 
                                type="button"
                                className="w-6 h-6 rounded border border-gray-300 bg-pink-500 hover:scale-110 transition-transform"
                                onClick={(e) => {
                                  applyFormatting('backColor', '#ec4899');
                                  setSelectedBgColor('#ec4899');
                                  setShowBgColorPalette(false);
                                }}
                                title="Розовый фон"
                              ></button>
                              <button 
                                type="button"
                                className="w-6 h-6 rounded border border-gray-300 bg-gray-500 hover:scale-110 transition-transform"
                                onClick={(e) => {
                                  applyFormatting('backColor', '#6b7280');
                                  setSelectedBgColor('#6b7280');
                                  setShowBgColorPalette(false);
                                }}
                                title="Серый фон"
                              ></button>
                              <button 
                                type="button"
                                className="w-6 h-6 rounded border border-gray-300 bg-indigo-500 hover:scale-110 transition-transform"
                                onClick={(e) => {
                                  applyFormatting('backColor', '#6366f1');
                                  setSelectedBgColor('#6366f1');
                                  setShowBgColorPalette(false);
                                }}
                                title="Индиго фон"
                              ></button>
                              <button 
                                type="button"
                                className="w-6 h-6 rounded border border-gray-300 bg-teal-500 hover:scale-110 transition-transform"
                                onClick={(e) => {
                                  applyFormatting('backColor', '#14b8a6');
                                  setSelectedBgColor('#14b8a6');
                                  setShowBgColorPalette(false);
                                }}
                                title="Бирюзовый фон"
                              ></button>
                            </div>
                            
                            {/* Кастомный цвет фона */}
                            <div className="border-t border-gray-300 pt-2">
                              <label className="text-xs text-gray-600 mb-1 block">Кастомный цвет фона:</label>
                              <input
                                type="color"
                                className="w-full h-8 rounded border border-gray-300 cursor-pointer"
                                onChange={(e) => {
                                  applyFormatting('backColor', e.target.value);
                                  setSelectedBgColor(e.target.value);
                                  setShowBgColorPalette(false);
                                }}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div
                    contentEditable
                    onInput={(e) => {
                      setNewQuestion({ ...newQuestion, question: e.target.innerHTML });
                      setActiveField('question');
                    }}
                    onFocus={() => setActiveField('question')}
                    suppressContentEditableWarning={true}
                    className="w-full p-4 border-0 resize-none focus:outline-none min-h-[60px] text-lg"
                    style={{ minHeight: '60px', outline: 'none' }}
                    data-placeholder="Введите вопрос"
                  />
                </div>
              </div>
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ответ
                </label>
                {/* Панель инструментов для ответа */}
                <div className="border border-gray-300 rounded-lg">
                  <div className="border-b border-gray-300 p-2 bg-gray-50 rounded-t-lg">
                    <div className="flex flex-wrap gap-2 items-center">
                      <select 
                        className="px-2 py-1 border border-gray-300 rounded text-sm"
                        value={selectedFont}
                        onChange={(e) => {
                          setSelectedFont(e.target.value);
                          if (e.target.value) {
                            applyFormatting('fontName', e.target.value);
                          }
                        }}
                      >
                        <option value="">Шрифт</option>
                        <option value="Arial, sans-serif">Arial</option>
                        <option value="Helvetica, sans-serif">Helvetica</option>
                        <option value="Times New Roman, serif">Times New Roman</option>
                        <option value="Georgia, serif">Georgia</option>
                        <option value="Verdana, sans-serif">Verdana</option>
                        <option value="Courier New, monospace">Courier New</option>
                        <option value="Roboto, sans-serif">Roboto</option>
                        <option value="Open Sans, sans-serif">Open Sans</option>
                        <option value="PT Sans, sans-serif">PT Sans</option>
                        <option value="PT Serif, serif">PT Serif</option>
                        <option value="Lora, serif">Lora</option>
                        <option value="Playfair Display, serif">Playfair Display</option>
                        <option value="Montserrat, sans-serif">Montserrat</option>
                        <option value="Raleway, sans-serif">Raleway</option>
                        <option value="Ubuntu, sans-serif">Ubuntu</option>
                      </select>
                      
                      <select 
                        className="px-2 py-1 border border-gray-300 rounded text-sm"
                        value={selectedSize}
                        onChange={(e) => {
                          setSelectedSize(e.target.value);
                          if (e.target.value) {
                            const editor = getActiveEditor();
                            if (editor) {
                              editor.focus();
                              
                              const selection = window.getSelection();
                              const range = selection.getRangeAt(0);
                              
                              if (!range.collapsed) {
                                // Если есть выделение - применяем к выделенному тексту
                                const span = document.createElement('span');
                                span.style.fontSize = e.target.value;
                                try {
                                  range.surroundContents(span);
                                } catch (e) {
                                  range.insertNode(span);
                                }
                              } else {
                                // Если нет выделения - выделяем слово под курсором или создаем выделение для будущего ввода
                                const wordRange = document.createRange();
                                
                                // Пытаемся выделить слово под курсором
                                try {
                                  wordRange.selectNodeContents(editor);
                                  const text = editor.textContent || editor.innerText || '';
                                  const cursorPosition = range.startOffset;
                                  
                                  // Ищем границы слова
                                  let start = cursorPosition;
                                  let end = cursorPosition;
                                  
                                  // Ищем начало слова
                                  while (start > 0 && /\S/.test(text[start - 1])) {
                                    start--;
                                  }
                                  
                                  // Ищем конец слова
                                  while (end < text.length && /\S/.test(text[end])) {
                                    end++;
                                  }
                                  
                                  // Если нашли слово - выделяем его
                                  if (start < end) {
                                    wordRange.setStart(editor.firstChild || editor, start);
                                    wordRange.setEnd(editor.firstChild || editor, end);
                                  } else {
                                    // Если слова нет - создаем пустое выделение для будущего ввода
                                    wordRange.setStart(range.startContainer, range.startOffset);
                                    wordRange.setEnd(range.startContainer, range.startOffset);
                                  }
                                } catch (error) {
                                  // Если не удалось выделить слово - создаем пустое выделение
                                  wordRange.setStart(range.startContainer, range.startOffset);
                                  wordRange.setEnd(range.startContainer, range.startOffset);
                                }
                                
                                // Применяем размер к выделению
                                const span = document.createElement('span');
                                span.style.fontSize = e.target.value;
                                span.setAttribute('data-font-size', e.target.value);
                                
                                if (wordRange.collapsed) {
                                  // Если выделение пустое - добавляем невидимый символ
                                  span.innerHTML = '&#8203;';
                                  wordRange.insertNode(span);
                                  range.setStart(span, 1);
                                } else {
                                  // Если есть выделение - оборачиваем его
                                  try {
                                    wordRange.surroundContents(span);
                                  } catch (e) {
                                    wordRange.insertNode(span);
                                  }
                                  range.setStartAfter(span);
                                }
                                
                                range.collapse(true);
                                selection.removeAllRanges();
                                selection.addRange(range);
                              }
                              
                              if (activeField === 'question') {
                                setNewQuestion({ ...newQuestion, question: editor.innerHTML });
                              } else {
                                setNewQuestion({ ...newQuestion, answer: editor.innerHTML });
                              }
                            }
                          }
                        }}
                      >
                        <option value="">Размер</option>
                        <option value="12px">12px</option>
                        <option value="14px">14px</option>
                        <option value="16px">16px</option>
                        <option value="18px">18px</option>
                        <option value="20px">20px</option>
                        <option value="24px">24px</option>
                        <option value="28px">28px</option>
                        <option value="32px">32px</option>
                        <option value="36px">36px</option>
                      </select>
                      
                      <button 
                        type="button"
                        className="px-2 py-1 border border-gray-300 rounded text-sm hover:bg-gray-100"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          applyFormatting('bold');
                        }}
                        title="Жирный"
                      >
                        B
                      </button>
                      
                      <button 
                        type="button"
                        className="px-2 py-1 border border-gray-300 rounded text-sm hover:bg-gray-100"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          applyFormatting('italic');
                        }}
                        title="Курсив"
                      >
                        I
                      </button>
                      
                      <button 
                        type="button"
                        className="px-2 py-1 border border-gray-300 rounded text-sm hover:bg-gray-100"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          applyFormatting('underline');
                        }}
                        title="Подчеркивание"
                      >
                        U
                      </button>
                      
                      <button 
                        type="button"
                        className="px-2 py-1 border border-gray-300 rounded text-sm hover:bg-gray-100"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          applyFormatting('insertUnorderedList');
                        }}
                        title="Маркированный список"
                      >
                        •
                      </button>
                      
                      <button 
                        type="button"
                        className="px-2 py-1 border border-gray-300 rounded text-sm hover:bg-gray-100"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          applyFormatting('insertOrderedList');
                        }}
                        title="Нумерованный список"
                      >
                        1.
                      </button>
                      
                      {/* Кнопка цвета текста */}
                      <div className="relative color-palette-container">
                        <button 
                          type="button"
                          className="w-8 h-8 border border-gray-300 rounded text-sm hover:bg-gray-100 flex items-center justify-center"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setShowColorPalette(!showColorPalette);
                          }}
                          title={`Цвет текста: ${selectedColor}`}
                        >
                          <span 
                            className="text-lg font-bold"
                            style={{ color: selectedColor }}
                          >
                            A
                          </span>
                        </button>
                        
                        {/* Выпадающая палитра цветов */}
                        {showColorPalette && (
                          <div className="absolute top-full left-0 mt-1 p-4 bg-white border border-gray-300 rounded-lg shadow-lg z-10 w-48">
                            <div className="grid grid-cols-6 gap-1.5 mb-3">
                              <button 
                                type="button"
                                className="w-6 h-6 rounded border border-gray-300 bg-black hover:scale-110 transition-transform"
                                onClick={() => {
                                  applyFormatting('foreColor', '#000000');
                                  setSelectedColor('#000000');
                                  setShowColorPalette(false);
                                }}
                                title="Черный"
                              ></button>
                              <button 
                                type="button"
                                className="w-6 h-6 rounded border border-gray-300 bg-white hover:scale-110 transition-transform"
                                onClick={(e) => {
                                  applyFormatting('foreColor', '#ffffff');
                                  setSelectedColor('#ffffff');
                                  setShowColorPalette(false);
                                }}
                                title="Белый"
                              ></button>
                              <button 
                                type="button"
                                className="w-6 h-6 rounded border border-gray-300 bg-red-500 hover:scale-110 transition-transform"
                                onClick={(e) => {
                                  applyFormatting('foreColor', '#ef4444');
                                  setSelectedColor('#ef4444');
                                  setShowColorPalette(false);
                                }}
                                title="Красный"
                              ></button>
                              <button 
                                type="button"
                                className="w-6 h-6 rounded border border-gray-300 bg-orange-500 hover:scale-110 transition-transform"
                                onClick={(e) => {
                                  applyFormatting('foreColor', '#f97316');
                                  setSelectedColor('#f97316');
                                  setShowColorPalette(false);
                                }}
                                title="Оранжевый"
                              ></button>
                              <button 
                                type="button"
                                className="w-6 h-6 rounded border border-gray-300 bg-yellow-500 hover:scale-110 transition-transform"
                                onClick={(e) => {
                                  applyFormatting('foreColor', '#eab308');
                                  setSelectedColor('#eab308');
                                  setShowColorPalette(false);
                                }}
                                title="Желтый"
                              ></button>
                              <button 
                                type="button"
                                className="w-6 h-6 rounded border border-gray-300 bg-green-500 hover:scale-110 transition-transform"
                                onClick={(e) => {
                                  applyFormatting('foreColor', '#22c55e');
                                  setSelectedColor('#22c55e');
                                  setShowColorPalette(false);
                                }}
                                title="Зеленый"
                              ></button>
                              <button 
                                type="button"
                                className="w-6 h-6 rounded border border-gray-300 bg-blue-500 hover:scale-110 transition-transform"
                                onClick={(e) => {
                                  applyFormatting('foreColor', '#3b82f6');
                                  setSelectedColor('#3b82f6');
                                  setShowColorPalette(false);
                                }}
                                title="Синий"
                              ></button>
                              <button 
                                type="button"
                                className="w-6 h-6 rounded border border-gray-300 bg-purple-500 hover:scale-110 transition-transform"
                                onClick={(e) => {
                                  applyFormatting('foreColor', '#a855f7');
                                  setSelectedColor('#a855f7');
                                  setShowColorPalette(false);
                                }}
                                title="Фиолетовый"
                              ></button>
                              <button 
                                type="button"
                                className="w-6 h-6 rounded border border-gray-300 bg-pink-500 hover:scale-110 transition-transform"
                                onClick={(e) => {
                                  applyFormatting('foreColor', '#ec4899');
                                  setSelectedColor('#ec4899');
                                  setShowColorPalette(false);
                                }}
                                title="Розовый"
                              ></button>
                              <button 
                                type="button"
                                className="w-6 h-6 rounded border border-gray-300 bg-gray-500 hover:scale-110 transition-transform"
                                onClick={(e) => {
                                  applyFormatting('foreColor', '#6b7280');
                                  setSelectedColor('#6b7280');
                                  setShowColorPalette(false);
                                }}
                                title="Серый"
                              ></button>
                              <button 
                                type="button"
                                className="w-6 h-6 rounded border border-gray-300 bg-indigo-500 hover:scale-110 transition-transform"
                                onClick={(e) => {
                                  applyFormatting('foreColor', '#6366f1');
                                  setSelectedColor('#6366f1');
                                  setShowColorPalette(false);
                                }}
                                title="Индиго"
                              ></button>
                              <button 
                                type="button"
                                className="w-6 h-6 rounded border border-gray-300 bg-teal-500 hover:scale-110 transition-transform"
                                onClick={(e) => {
                                  applyFormatting('foreColor', '#14b8a6');
                                  setSelectedColor('#14b8a6');
                                  setShowColorPalette(false);
                                }}
                                title="Бирюзовый"
                              ></button>
                            </div>
                            
                            {/* Кастомный цвет */}
                            <div className="border-t border-gray-300 pt-2">
                              <label className="text-xs text-gray-600 mb-1 block">Кастомный цвет:</label>
                              <input
                                type="color"
                                className="w-full h-8 rounded border border-gray-300 cursor-pointer"
                                onChange={(e) => {
                                  applyFormatting('foreColor', e.target.value);
                                  setSelectedColor(e.target.value);
                                  setShowColorPalette(false);
                                }}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                      
                      {/* Кнопка цвета фона */}
                      <div className="relative bg-color-palette-container">
                        <button 
                          type="button"
                          className="w-8 h-8 border border-gray-300 rounded text-sm hover:bg-gray-100 flex items-center justify-center relative"
                          style={{ backgroundColor: selectedBgColor === 'transparent' ? 'white' : selectedBgColor }}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setShowBgColorPalette(!showBgColorPalette);
                          }}
                          title={`Цвет фона: ${selectedBgColor}`}
                        >
                          {selectedBgColor === 'transparent' && (
                            <div className="absolute inset-0 bg-gradient-to-br from-red-200 to-blue-200 opacity-50 rounded"></div>
                          )}
                          <span 
                            className="text-lg font-bold relative z-10"
                            style={{ 
                              color: selectedBgColor === '#ffffff' || selectedBgColor === '#ffff00' || selectedBgColor === 'transparent' ? '#000000' : '#ffffff'
                            }}
                          >
                            A
                          </span>
                        </button>
                        
                        {/* Выпадающая палитра цветов фона */}
                        {showBgColorPalette && (
                          <div className="absolute top-full left-0 mt-1 p-4 bg-white border border-gray-300 rounded-lg shadow-lg z-10 w-48">
                            <div className="grid grid-cols-6 gap-1.5 mb-3">
                              <button 
                                type="button"
                                className="w-6 h-6 rounded border border-gray-300 bg-transparent hover:scale-110 transition-transform relative"
                                onClick={(e) => {
                                  applyFormatting('backColor', 'transparent');
                                  setSelectedBgColor('transparent');
                                  setShowBgColorPalette(false);
                                }}
                                title="Прозрачный фон"
                              >
                                <div className="absolute inset-0 bg-gradient-to-br from-red-200 to-blue-200 opacity-50 rounded"></div>
                                <div className="absolute inset-1 bg-white rounded-sm"></div>
                              </button>
                              
                              <button 
                                type="button"
                                className="w-6 h-6 rounded border border-gray-300 bg-black hover:scale-110 transition-transform"
                                onClick={(e) => {
                                  applyFormatting('backColor', '#000000');
                                  setSelectedBgColor('#000000');
                                  setShowBgColorPalette(false);
                                }}
                                title="Черный фон"
                              ></button>
                              <button 
                                type="button"
                                className="w-6 h-6 rounded border border-gray-300 bg-white hover:scale-110 transition-transform"
                                onClick={(e) => {
                                  applyFormatting('backColor', '#ffffff');
                                  setSelectedBgColor('#ffffff');
                                  setShowBgColorPalette(false);
                                }}
                                title="Белый фон"
                              ></button>
                              <button 
                                type="button"
                                className="w-6 h-6 rounded border border-gray-300 bg-red-500 hover:scale-110 transition-transform"
                                onClick={(e) => {
                                  applyFormatting('backColor', '#ef4444');
                                  setSelectedBgColor('#ef4444');
                                  setShowBgColorPalette(false);
                                }}
                                title="Красный фон"
                              ></button>
                              <button 
                                type="button"
                                className="w-6 h-6 rounded border border-gray-300 bg-orange-500 hover:scale-110 transition-transform"
                                onClick={(e) => {
                                  applyFormatting('backColor', '#f97316');
                                  setSelectedBgColor('#f97316');
                                  setShowBgColorPalette(false);
                                }}
                                title="Оранжевый фон"
                              ></button>
                              <button 
                                type="button"
                                className="w-6 h-6 rounded border border-gray-300 bg-yellow-500 hover:scale-110 transition-transform"
                                onClick={(e) => {
                                  applyFormatting('backColor', '#eab308');
                                  setSelectedBgColor('#eab308');
                                  setShowBgColorPalette(false);
                                }}
                                title="Желтый фон"
                              ></button>
                              <button 
                                type="button"
                                className="w-6 h-6 rounded border border-gray-300 bg-green-500 hover:scale-110 transition-transform"
                                onClick={(e) => {
                                  applyFormatting('backColor', '#22c55e');
                                  setSelectedBgColor('#22c55e');
                                  setShowBgColorPalette(false);
                                }}
                                title="Зеленый фон"
                              ></button>
                              <button 
                                type="button"
                                className="w-6 h-6 rounded border border-gray-300 bg-blue-500 hover:scale-110 transition-transform"
                                onClick={(e) => {
                                  applyFormatting('backColor', '#3b82f6');
                                  setSelectedBgColor('#3b82f6');
                                  setShowBgColorPalette(false);
                                }}
                                title="Синий фон"
                              ></button>
                              <button 
                                type="button"
                                className="w-6 h-6 rounded border border-gray-300 bg-purple-500 hover:scale-110 transition-transform"
                                onClick={(e) => {
                                  applyFormatting('backColor', '#a855f7');
                                  setSelectedBgColor('#a855f7');
                                  setShowBgColorPalette(false);
                                }}
                                title="Фиолетовый фон"
                              ></button>
                              <button 
                                type="button"
                                className="w-6 h-6 rounded border border-gray-300 bg-pink-500 hover:scale-110 transition-transform"
                                onClick={(e) => {
                                  applyFormatting('backColor', '#ec4899');
                                  setSelectedBgColor('#ec4899');
                                  setShowBgColorPalette(false);
                                }}
                                title="Розовый фон"
                              ></button>
                              <button 
                                type="button"
                                className="w-6 h-6 rounded border border-gray-300 bg-gray-500 hover:scale-110 transition-transform"
                                onClick={(e) => {
                                  applyFormatting('backColor', '#6b7280');
                                  setSelectedBgColor('#6b7280');
                                  setShowBgColorPalette(false);
                                }}
                                title="Серый фон"
                              ></button>
                              <button 
                                type="button"
                                className="w-6 h-6 rounded border border-gray-300 bg-indigo-500 hover:scale-110 transition-transform"
                                onClick={(e) => {
                                  applyFormatting('backColor', '#6366f1');
                                  setSelectedBgColor('#6366f1');
                                  setShowBgColorPalette(false);
                                }}
                                title="Индиго фон"
                              ></button>
                              <button 
                                type="button"
                                className="w-6 h-6 rounded border border-gray-300 bg-teal-500 hover:scale-110 transition-transform"
                                onClick={(e) => {
                                  applyFormatting('backColor', '#14b8a6');
                                  setSelectedBgColor('#14b8a6');
                                  setShowBgColorPalette(false);
                                }}
                                title="Бирюзовый фон"
                              ></button>
                            </div>
                            
                            {/* Кастомный цвет фона */}
                            <div className="border-t border-gray-300 pt-2">
                              <label className="text-xs text-gray-600 mb-1 block">Кастомный цвет фона:</label>
                              <input
                                type="color"
                                className="w-full h-8 rounded border border-gray-300 cursor-pointer"
                                onChange={(e) => {
                                  applyFormatting('backColor', e.target.value);
                                  setSelectedBgColor(e.target.value);
                                  setShowBgColorPalette(false);
                                }}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div
                    contentEditable
                    onInput={(e) => {
                      setNewQuestion({ ...newQuestion, answer: e.target.innerHTML });
                      setActiveField('answer');
                    }}
                    onFocus={() => setActiveField('answer')}
                    suppressContentEditableWarning={true}
                    className="w-full p-4 border-0 resize-none focus:outline-none min-h-[120px]"
                    style={{ minHeight: '120px', outline: 'none' }}
                    data-placeholder="Введите ответ"
                  />
                </div>
              </div>
              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowQuestionModal(false);
                    setSelectedCategoryIndex(null);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Отмена
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
                >
                  Добавить
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Category Modal */}
      {showEditCategoryModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowEditCategoryModal(false);
              setSelectedCategoryIndex(null);
            }
          }}
        >
          <div 
            className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-xl font-semibold mb-4">Редактировать категорию</h2>
            <form onSubmit={updateCategory}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Название категории
                </label>
                
                {/* Панель инструментов для редактирования названия категории */}
                <div className="border border-gray-300 rounded-lg">
                  <div className="border-b border-gray-300 p-2 bg-gray-50 rounded-t-lg">
                    <div className="flex flex-wrap gap-2 items-center">
                      <select 
                        className="px-2 py-1 border border-gray-300 rounded text-sm"
                        value={selectedFont}
                        onChange={(e) => {
                          setSelectedFont(e.target.value);
                          if (e.target.value) {
                            applyFormatting('fontName', e.target.value);
                          }
                        }}
                      >
                        <option value="">Шрифт</option>
                        <option value="Arial, sans-serif">Arial</option>
                        <option value="Helvetica, sans-serif">Helvetica</option>
                        <option value="Times New Roman, serif">Times New Roman</option>
                        <option value="Georgia, serif">Georgia</option>
                        <option value="Verdana, sans-serif">Verdana</option>
                        <option value="Courier New, monospace">Courier New</option>
                        <option value="Roboto, sans-serif">Roboto</option>
                        <option value="Open Sans, sans-serif">Open Sans</option>
                        <option value="PT Sans, sans-serif">PT Sans</option>
                        <option value="PT Serif, serif">PT Serif</option>
                        <option value="Lora, serif">Lora</option>
                        <option value="Playfair Display, serif">Playfair Display</option>
                        <option value="Montserrat, sans-serif">Montserrat</option>
                        <option value="Raleway, sans-serif">Raleway</option>
                        <option value="Ubuntu, sans-serif">Ubuntu</option>
                      </select>
                      
                      <select 
                        className="px-2 py-1 border border-gray-300 rounded text-sm"
                        value={selectedSize}
                        onChange={(e) => {
                          setSelectedSize(e.target.value);
                          if (e.target.value) {
                            const editor = getActiveEditor();
                            if (editor) {
                              editor.focus();
                              
                              const selection = window.getSelection();
                              const range = selection.getRangeAt(0);
                              
                              if (!range.collapsed) {
                                // Если есть выделение - применяем к выделенному тексту
                                const span = document.createElement('span');
                                span.style.fontSize = e.target.value;
                                try {
                                  range.surroundContents(span);
                                } catch (e) {
                                  range.insertNode(span);
                                }
                              } else {
                                // Если нет выделения - выделяем слово под курсором или создаем выделение для будущего ввода
                                const wordRange = document.createRange();
                                
                                // Пытаемся выделить слово под курсором
                                try {
                                  wordRange.selectNodeContents(editor);
                                  const text = editor.textContent || editor.innerText || '';
                                  const cursorPosition = range.startOffset;
                                  
                                  // Ищем границы слова
                                  let start = cursorPosition;
                                  let end = cursorPosition;
                                  
                                  // Ищем начало слова
                                  while (start > 0 && /\S/.test(text[start - 1])) {
                                    start--;
                                  }
                                  
                                  // Ищем конец слова
                                  while (end < text.length && /\S/.test(text[end])) {
                                    end++;
                                  }
                                  
                                  // Если нашли слово - выделяем его
                                  if (start < end) {
                                    wordRange.setStart(editor.firstChild || editor, start);
                                    wordRange.setEnd(editor.firstChild || editor, end);
                                  } else {
                                    // Если слова нет - создаем пустое выделение для будущего ввода
                                    wordRange.setStart(range.startContainer, range.startOffset);
                                    wordRange.setEnd(range.startContainer, range.startOffset);
                                  }
                                } catch (error) {
                                  // Если не удалось выделить слово - создаем пустое выделение
                                  wordRange.setStart(range.startContainer, range.startOffset);
                                  wordRange.setEnd(range.startContainer, range.startOffset);
                                }
                                
                                // Применяем размер к выделению
                                const span = document.createElement('span');
                                span.style.fontSize = e.target.value;
                                span.setAttribute('data-font-size', e.target.value);
                                
                                if (wordRange.collapsed) {
                                  // Если выделение пустое - добавляем невидимый символ
                                  span.innerHTML = '&#8203;';
                                  wordRange.insertNode(span);
                                  range.setStart(span, 1);
                                } else {
                                  // Если есть выделение - оборачиваем его
                                  try {
                                    wordRange.surroundContents(span);
                                  } catch (e) {
                                    wordRange.insertNode(span);
                                  }
                                  range.setStartAfter(span);
                                }
                                
                                range.collapse(true);
                                selection.removeAllRanges();
                                selection.addRange(range);
                              }
                            }
                          }
                        }}
                      >
                        <option value="">Размер</option>
                        <option value="12px">12px</option>
                        <option value="14px">14px</option>
                        <option value="16px">16px</option>
                        <option value="18px">18px</option>
                        <option value="20px">20px</option>
                        <option value="24px">24px</option>
                        <option value="28px">28px</option>
                        <option value="32px">32px</option>
                        <option value="36px">36px</option>
                        <option value="40px">40px</option>
                        <option value="48px">48px</option>
                      </select>
                      
                      <button
                        type="button"
                        className="px-2 py-1 border border-gray-300 rounded text-sm hover:bg-gray-100"
                        onClick={() => applyFormatting('bold')}
                        title="Жирный"
                      >
                        <strong>B</strong>
                      </button>
                      
                      <button
                        type="button"
                        className="px-2 py-1 border border-gray-300 rounded text-sm hover:bg-gray-100"
                        onClick={() => applyFormatting('italic')}
                        title="Курсив"
                      >
                        <em>I</em>
                      </button>
                      
                      <button
                        type="button"
                        className="px-2 py-1 border border-gray-300 rounded text-sm hover:bg-gray-100"
                        onClick={() => applyFormatting('underline')}
                        title="Подчеркнутый"
                      >
                        <u>U</u>
                      </button>
                      
                      <div className="relative">
                        <button
                          type="button"
                          className="px-2 py-1 border border-gray-300 rounded text-sm hover:bg-gray-100 flex items-center gap-1"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowColorPalette(!showColorPalette);
                          }}
                          title={`Цвет текста: ${selectedColor}`}
                        >
                          <span 
                            className="text-lg font-bold"
                            style={{ color: selectedColor }}
                          >
                            A
                          </span>
                        </button>
                        
                        {showColorPalette && (
                          <div className="absolute top-full left-0 mt-1 p-2 bg-white border border-gray-300 rounded-lg shadow-lg z-10">
                            <div className="grid grid-cols-8 gap-1">
                              {['#000000', '#333333', '#666666', '#999999', '#CCCCCC', '#FFFFFF', '#FF0000', '#00FF00', '#0000FF', '#FFFF00', '#FF00FF', '#00FFFF', '#FFA500', '#800080', '#008000', '#FFC0CB'].map(color => (
                                <button
                                  key={color}
                                  type="button"
                                  className="w-6 h-6 border border-gray-300 rounded hover:scale-110 transition-transform"
                                  style={{ backgroundColor: color }}
                                  onClick={() => {
                                    setSelectedColor(color);
                                    applyFormatting('foreColor', color);
                                    setShowColorPalette(false);
                                  }}
                                />
                              ))}
              </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div
                    ref={(el) => {
                      if (el && !el.dataset.initialized) {
                        el.innerHTML = editingCategory.name;
                        el.dataset.initialized = 'true';
                      }
                    }}
                    contentEditable
                    onInput={(e) => {
                      setEditingCategory({ ...editingCategory, name: e.target.innerHTML });
                      setActiveField('question');
                    }}
                    onFocus={() => setActiveField('question')}
                    suppressContentEditableWarning={true}
                    className="w-full p-4 border-0 resize-none focus:outline-none min-h-[60px] text-lg"
                    style={{ minHeight: '60px', outline: 'none' }}
                    data-placeholder="Введите название категории"
                  />
                </div>
              </div>
              
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Описание (необязательно)
                </label>
                
                <div
                  ref={(el) => {
                    if (el && !el.dataset.initialized) {
                      el.innerHTML = editingCategory.description;
                      el.dataset.initialized = 'true';
                    }
                  }}
                  contentEditable
                  onInput={(e) => {
                    setEditingCategory({ ...editingCategory, description: e.target.innerHTML });
                    setActiveField('answer');
                  }}
                  onFocus={() => setActiveField('answer')}
                  suppressContentEditableWarning={true}
                  className="w-full p-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 min-h-[120px]"
                  style={{ 
                    minHeight: '120px', 
                    outline: 'none',
                    listStyleType: 'disc'
                  }}
                  data-placeholder="Введите описание категории"
                />
              </div>
              
              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={() => setShowEditCategoryModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Отмена
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
                >
                  Сохранить
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Question Modal */}
      {showEditQuestionModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowEditQuestionModal(false);
              setSelectedCategoryIndex(null);
              setSelectedQuestionIndex(null);
            }
          }}
        >
          <div 
            className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-xl font-semibold mb-4">Редактировать вопрос</h2>
            <form onSubmit={updateQuestion}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Вопрос
                </label>
                
                {/* Панель инструментов для редактирования вопроса */}
                <div className="border border-gray-300 rounded-lg">
                  <div className="border-b border-gray-300 p-2 bg-gray-50 rounded-t-lg">
                    <div className="flex flex-wrap gap-2 items-center">
                      <select 
                        className="px-2 py-1 border border-gray-300 rounded text-sm"
                        value={selectedFont}
                        onChange={(e) => {
                          setSelectedFont(e.target.value);
                          if (e.target.value) {
                            applyFormatting('fontName', e.target.value);
                          }
                        }}
                      >
                        <option value="">Шрифт</option>
                        <option value="Arial, sans-serif">Arial</option>
                        <option value="Helvetica, sans-serif">Helvetica</option>
                        <option value="Times New Roman, serif">Times New Roman</option>
                        <option value="Georgia, serif">Georgia</option>
                        <option value="Verdana, sans-serif">Verdana</option>
                        <option value="Courier New, monospace">Courier New</option>
                        <option value="Roboto, sans-serif">Roboto</option>
                        <option value="Open Sans, sans-serif">Open Sans</option>
                        <option value="PT Sans, sans-serif">PT Sans</option>
                        <option value="PT Serif, serif">PT Serif</option>
                        <option value="Lora, serif">Lora</option>
                        <option value="Playfair Display, serif">Playfair Display</option>
                        <option value="Montserrat, sans-serif">Montserrat</option>
                        <option value="Raleway, sans-serif">Raleway</option>
                        <option value="Ubuntu, sans-serif">Ubuntu</option>
                      </select>
                      
                      <select 
                        className="px-2 py-1 border border-gray-300 rounded text-sm"
                        value={selectedSize}
                        onChange={(e) => {
                          setSelectedSize(e.target.value);
                          if (e.target.value) {
                            const editor = getActiveEditor();
                            if (editor) {
                              editor.focus();
                              
                              const selection = window.getSelection();
                              const range = selection.getRangeAt(0);
                              
                              if (!range.collapsed) {
                                // Если есть выделение - применяем к выделенному тексту
                                const span = document.createElement('span');
                                span.style.fontSize = e.target.value;
                                try {
                                  range.surroundContents(span);
                                } catch (e) {
                                  range.insertNode(span);
                                }
                              } else {
                                // Если нет выделения - выделяем слово под курсором или создаем выделение для будущего ввода
                                const wordRange = document.createRange();
                                
                                // Пытаемся выделить слово под курсором
                                try {
                                  wordRange.selectNodeContents(editor);
                                  const text = editor.textContent || editor.innerText || '';
                                  const cursorPosition = range.startOffset;
                                  
                                  // Ищем границы слова
                                  let start = cursorPosition;
                                  let end = cursorPosition;
                                  
                                  // Ищем начало слова
                                  while (start > 0 && /\S/.test(text[start - 1])) {
                                    start--;
                                  }
                                  
                                  // Ищем конец слова
                                  while (end < text.length && /\S/.test(text[end])) {
                                    end++;
                                  }
                                  
                                  // Если нашли слово - выделяем его
                                  if (start < end) {
                                    wordRange.setStart(editor.firstChild || editor, start);
                                    wordRange.setEnd(editor.firstChild || editor, end);
                                  } else {
                                    // Если слова нет - создаем пустое выделение для будущего ввода
                                    wordRange.setStart(range.startContainer, range.startOffset);
                                    wordRange.setEnd(range.startContainer, range.startOffset);
                                  }
                                } catch (error) {
                                  // Если не удалось выделить слово - создаем пустое выделение
                                  wordRange.setStart(range.startContainer, range.startOffset);
                                  wordRange.setEnd(range.startContainer, range.startOffset);
                                }
                                
                                // Применяем размер к выделению
                                const span = document.createElement('span');
                                span.style.fontSize = e.target.value;
                                span.setAttribute('data-font-size', e.target.value);
                                
                                if (wordRange.collapsed) {
                                  // Если выделение пустое - добавляем невидимый символ
                                  span.innerHTML = '&#8203;';
                                  wordRange.insertNode(span);
                                  range.setStart(span, 1);
                                } else {
                                  // Если есть выделение - оборачиваем его
                                  try {
                                    wordRange.surroundContents(span);
                                  } catch (e) {
                                    wordRange.insertNode(span);
                                  }
                                  range.setStartAfter(span);
                                }
                                
                                range.collapse(true);
                                selection.removeAllRanges();
                                selection.addRange(range);
                              }
                              
                              if (activeField === 'question') {
                                setEditingQuestion({ ...editingQuestion, question: editor.innerHTML });
                              } else {
                                setEditingQuestion({ ...editingQuestion, answer: editor.innerHTML });
                              }
                            }
                          }
                        }}
                      >
                        <option value="">Размер</option>
                        <option value="12px">12px</option>
                        <option value="14px">14px</option>
                        <option value="16px">16px</option>
                        <option value="18px">18px</option>
                        <option value="20px">20px</option>
                        <option value="24px">24px</option>
                        <option value="28px">28px</option>
                        <option value="32px">32px</option>
                        <option value="36px">36px</option>
                      </select>
                      
                      <button 
                        type="button"
                        className="px-2 py-1 border border-gray-300 rounded text-sm hover:bg-gray-100"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          applyFormatting('bold');
                        }}
                        title="Жирный"
                      >
                        B
                      </button>
                      
                      <button 
                        type="button"
                        className="px-2 py-1 border border-gray-300 rounded text-sm hover:bg-gray-100"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          applyFormatting('italic');
                        }}
                        title="Курсив"
                      >
                        I
                      </button>
                      
                      <button 
                        type="button"
                        className="px-2 py-1 border border-gray-300 rounded text-sm hover:bg-gray-100"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          applyFormatting('underline');
                        }}
                        title="Подчеркивание"
                      >
                        U
                      </button>
                      
                      <button 
                        type="button"
                        className="px-2 py-1 border border-gray-300 rounded text-sm hover:bg-gray-100"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          applyFormatting('insertUnorderedList');
                        }}
                        title="Маркированный список"
                      >
                        •
                      </button>
                      
                      <button 
                        type="button"
                        className="px-2 py-1 border border-gray-300 rounded text-sm hover:bg-gray-100"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          applyFormatting('insertOrderedList');
                        }}
                        title="Нумерованный список"
                      >
                        1.
                      </button>
                      
                      {/* Кнопка цвета текста */}
                      <div className="relative color-palette-container">
                        <button 
                          type="button"
                          className="w-8 h-8 border border-gray-300 rounded text-sm hover:bg-gray-100 flex items-center justify-center"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setShowColorPalette(!showColorPalette);
                          }}
                          title={`Цвет текста: ${selectedColor}`}
                        >
                          <span 
                            className="text-lg font-bold"
                            style={{ color: selectedColor }}
                          >
                            A
                          </span>
                        </button>
                        
                        {/* Выпадающая палитра цветов */}
                        {showColorPalette && (
                          <div className="absolute top-full left-0 mt-1 p-4 bg-white border border-gray-300 rounded-lg shadow-lg z-10 w-48">
                            <div className="grid grid-cols-6 gap-1.5 mb-3">
                              <button 
                                type="button"
                                className="w-6 h-6 rounded border border-gray-300 bg-black hover:scale-110 transition-transform"
                  onClick={() => {
                                  applyFormatting('foreColor', '#000000');
                                  setSelectedColor('#000000');
                                  setShowColorPalette(false);
                                }}
                                title="Черный"
                              ></button>
                              <button 
                                type="button"
                                className="w-6 h-6 rounded border border-gray-300 bg-white hover:scale-110 transition-transform"
                                onClick={(e) => {
                                  applyFormatting('foreColor', '#ffffff');
                                  setSelectedColor('#ffffff');
                                  setShowColorPalette(false);
                                }}
                                title="Белый"
                              ></button>
                              <button 
                                type="button"
                                className="w-6 h-6 rounded border border-gray-300 bg-red-500 hover:scale-110 transition-transform"
                                onClick={(e) => {
                                  applyFormatting('foreColor', '#ef4444');
                                  setSelectedColor('#ef4444');
                                  setShowColorPalette(false);
                                }}
                                title="Красный"
                              ></button>
                              <button 
                                type="button"
                                className="w-6 h-6 rounded border border-gray-300 bg-orange-500 hover:scale-110 transition-transform"
                                onClick={(e) => {
                                  applyFormatting('foreColor', '#f97316');
                                  setSelectedColor('#f97316');
                                  setShowColorPalette(false);
                                }}
                                title="Оранжевый"
                              ></button>
                              <button 
                                type="button"
                                className="w-6 h-6 rounded border border-gray-300 bg-yellow-500 hover:scale-110 transition-transform"
                                onClick={(e) => {
                                  applyFormatting('foreColor', '#eab308');
                                  setSelectedColor('#eab308');
                                  setShowColorPalette(false);
                                }}
                                title="Желтый"
                              ></button>
                              <button 
                                type="button"
                                className="w-6 h-6 rounded border border-gray-300 bg-green-500 hover:scale-110 transition-transform"
                                onClick={(e) => {
                                  applyFormatting('foreColor', '#22c55e');
                                  setSelectedColor('#22c55e');
                                  setShowColorPalette(false);
                                }}
                                title="Зеленый"
                              ></button>
                              <button 
                                type="button"
                                className="w-6 h-6 rounded border border-gray-300 bg-blue-500 hover:scale-110 transition-transform"
                                onClick={(e) => {
                                  applyFormatting('foreColor', '#3b82f6');
                                  setSelectedColor('#3b82f6');
                                  setShowColorPalette(false);
                                }}
                                title="Синий"
                              ></button>
                              <button 
                                type="button"
                                className="w-6 h-6 rounded border border-gray-300 bg-purple-500 hover:scale-110 transition-transform"
                                onClick={(e) => {
                                  applyFormatting('foreColor', '#a855f7');
                                  setSelectedColor('#a855f7');
                                  setShowColorPalette(false);
                                }}
                                title="Фиолетовый"
                              ></button>
                              <button 
                                type="button"
                                className="w-6 h-6 rounded border border-gray-300 bg-pink-500 hover:scale-110 transition-transform"
                                onClick={(e) => {
                                  applyFormatting('foreColor', '#ec4899');
                                  setSelectedColor('#ec4899');
                                  setShowColorPalette(false);
                                }}
                                title="Розовый"
                              ></button>
                              <button 
                                type="button"
                                className="w-6 h-6 rounded border border-gray-300 bg-gray-500 hover:scale-110 transition-transform"
                                onClick={(e) => {
                                  applyFormatting('foreColor', '#6b7280');
                                  setSelectedColor('#6b7280');
                                  setShowColorPalette(false);
                                }}
                                title="Серый"
                              ></button>
                              <button 
                                type="button"
                                className="w-6 h-6 rounded border border-gray-300 bg-indigo-500 hover:scale-110 transition-transform"
                                onClick={(e) => {
                                  applyFormatting('foreColor', '#6366f1');
                                  setSelectedColor('#6366f1');
                                  setShowColorPalette(false);
                                }}
                                title="Индиго"
                              ></button>
                              <button 
                                type="button"
                                className="w-6 h-6 rounded border border-gray-300 bg-teal-500 hover:scale-110 transition-transform"
                                onClick={(e) => {
                                  applyFormatting('foreColor', '#14b8a6');
                                  setSelectedColor('#14b8a6');
                                  setShowColorPalette(false);
                                }}
                                title="Бирюзовый"
                              ></button>
                            </div>
                            
                            {/* Кастомный цвет */}
                            <div className="border-t border-gray-300 pt-2">
                              <label className="text-xs text-gray-600 mb-1 block">Кастомный цвет:</label>
                              <input
                                type="color"
                                className="w-full h-8 rounded border border-gray-300 cursor-pointer"
                                onChange={(e) => {
                                  applyFormatting('foreColor', e.target.value);
                                  setSelectedColor(e.target.value);
                                  setShowColorPalette(false);
                                }}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                      
                      {/* Кнопка цвета фона */}
                      <div className="relative bg-color-palette-container">
                        <button 
                          type="button"
                          className="w-8 h-8 border border-gray-300 rounded text-sm hover:bg-gray-100 flex items-center justify-center relative"
                          style={{ backgroundColor: selectedBgColor === 'transparent' ? 'white' : selectedBgColor }}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setShowBgColorPalette(!showBgColorPalette);
                          }}
                          title={`Цвет фона: ${selectedBgColor}`}
                        >
                          {selectedBgColor === 'transparent' && (
                            <div className="absolute inset-0 bg-gradient-to-br from-red-200 to-blue-200 opacity-50 rounded"></div>
                          )}
                          <span 
                            className="text-lg font-bold relative z-10"
                            style={{ 
                              color: selectedBgColor === '#ffffff' || selectedBgColor === '#ffff00' || selectedBgColor === 'transparent' ? '#000000' : '#ffffff'
                            }}
                          >
                            A
                          </span>
                        </button>
                        
                        {/* Выпадающая палитра цветов фона */}
                        {showBgColorPalette && (
                          <div className="absolute top-full left-0 mt-1 p-4 bg-white border border-gray-300 rounded-lg shadow-lg z-10 w-48">
                            <div className="grid grid-cols-6 gap-1.5 mb-3">
                              <button 
                                type="button"
                                className="w-6 h-6 rounded border border-gray-300 bg-transparent hover:scale-110 transition-transform relative"
                                onClick={(e) => {
                                  applyFormatting('backColor', 'transparent');
                                  setSelectedBgColor('transparent');
                                  setShowBgColorPalette(false);
                                }}
                                title="Прозрачный фон"
                              >
                                <div className="absolute inset-0 bg-gradient-to-br from-red-200 to-blue-200 opacity-50 rounded"></div>
                                <div className="absolute inset-1 bg-white rounded-sm"></div>
                              </button>
                              
                              <button 
                                type="button"
                                className="w-6 h-6 rounded border border-gray-300 bg-black hover:scale-110 transition-transform"
                                onClick={(e) => {
                                  applyFormatting('backColor', '#000000');
                                  setSelectedBgColor('#000000');
                                  setShowBgColorPalette(false);
                                }}
                                title="Черный фон"
                              ></button>
                              <button 
                                type="button"
                                className="w-6 h-6 rounded border border-gray-300 bg-white hover:scale-110 transition-transform"
                                onClick={(e) => {
                                  applyFormatting('backColor', '#ffffff');
                                  setSelectedBgColor('#ffffff');
                                  setShowBgColorPalette(false);
                                }}
                                title="Белый фон"
                              ></button>
                              <button 
                                type="button"
                                className="w-6 h-6 rounded border border-gray-300 bg-red-500 hover:scale-110 transition-transform"
                                onClick={(e) => {
                                  applyFormatting('backColor', '#ef4444');
                                  setSelectedBgColor('#ef4444');
                                  setShowBgColorPalette(false);
                                }}
                                title="Красный фон"
                              ></button>
                              <button 
                                type="button"
                                className="w-6 h-6 rounded border border-gray-300 bg-orange-500 hover:scale-110 transition-transform"
                                onClick={(e) => {
                                  applyFormatting('backColor', '#f97316');
                                  setSelectedBgColor('#f97316');
                                  setShowBgColorPalette(false);
                                }}
                                title="Оранжевый фон"
                              ></button>
                              <button 
                                type="button"
                                className="w-6 h-6 rounded border border-gray-300 bg-yellow-500 hover:scale-110 transition-transform"
                                onClick={(e) => {
                                  applyFormatting('backColor', '#eab308');
                                  setSelectedBgColor('#eab308');
                                  setShowBgColorPalette(false);
                                }}
                                title="Желтый фон"
                              ></button>
                              <button 
                                type="button"
                                className="w-6 h-6 rounded border border-gray-300 bg-green-500 hover:scale-110 transition-transform"
                                onClick={(e) => {
                                  applyFormatting('backColor', '#22c55e');
                                  setSelectedBgColor('#22c55e');
                                  setShowBgColorPalette(false);
                                }}
                                title="Зеленый фон"
                              ></button>
                              <button 
                                type="button"
                                className="w-6 h-6 rounded border border-gray-300 bg-blue-500 hover:scale-110 transition-transform"
                                onClick={(e) => {
                                  applyFormatting('backColor', '#3b82f6');
                                  setSelectedBgColor('#3b82f6');
                                  setShowBgColorPalette(false);
                                }}
                                title="Синий фон"
                              ></button>
                              <button 
                                type="button"
                                className="w-6 h-6 rounded border border-gray-300 bg-purple-500 hover:scale-110 transition-transform"
                                onClick={(e) => {
                                  applyFormatting('backColor', '#a855f7');
                                  setSelectedBgColor('#a855f7');
                                  setShowBgColorPalette(false);
                                }}
                                title="Фиолетовый фон"
                              ></button>
                              <button 
                                type="button"
                                className="w-6 h-6 rounded border border-gray-300 bg-pink-500 hover:scale-110 transition-transform"
                                onClick={(e) => {
                                  applyFormatting('backColor', '#ec4899');
                                  setSelectedBgColor('#ec4899');
                                  setShowBgColorPalette(false);
                                }}
                                title="Розовый фон"
                              ></button>
                              <button 
                                type="button"
                                className="w-6 h-6 rounded border border-gray-300 bg-gray-500 hover:scale-110 transition-transform"
                                onClick={(e) => {
                                  applyFormatting('backColor', '#6b7280');
                                  setSelectedBgColor('#6b7280');
                                  setShowBgColorPalette(false);
                                }}
                                title="Серый фон"
                              ></button>
                              <button 
                                type="button"
                                className="w-6 h-6 rounded border border-gray-300 bg-indigo-500 hover:scale-110 transition-transform"
                                onClick={(e) => {
                                  applyFormatting('backColor', '#6366f1');
                                  setSelectedBgColor('#6366f1');
                                  setShowBgColorPalette(false);
                                }}
                                title="Индиго фон"
                              ></button>
                              <button 
                                type="button"
                                className="w-6 h-6 rounded border border-gray-300 bg-teal-500 hover:scale-110 transition-transform"
                                onClick={(e) => {
                                  applyFormatting('backColor', '#14b8a6');
                                  setSelectedBgColor('#14b8a6');
                                  setShowBgColorPalette(false);
                                }}
                                title="Бирюзовый фон"
                              ></button>
                            </div>
                            
                            {/* Кастомный цвет фона */}
                            <div className="border-t border-gray-300 pt-2">
                              <label className="text-xs text-gray-600 mb-1 block">Кастомный цвет фона:</label>
                              <input
                                type="color"
                                className="w-full h-8 rounded border border-gray-300 cursor-pointer"
                                onChange={(e) => {
                                  applyFormatting('backColor', e.target.value);
                                  setSelectedBgColor(e.target.value);
                                  setShowBgColorPalette(false);
                                }}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div
                    ref={(el) => {
                      if (el && !el.dataset.initialized) {
                        el.innerHTML = editingQuestion.question;
                        el.dataset.initialized = 'true';
                      }
                    }}
                    contentEditable
                    onInput={(e) => {
                      setEditingQuestion({ ...editingQuestion, question: e.target.innerHTML });
                      setActiveField('question');
                    }}
                    onFocus={() => setActiveField('question')}
                    suppressContentEditableWarning={true}
                    className="w-full p-4 border-0 resize-none focus:outline-none min-h-[60px] text-lg"
                    style={{ minHeight: '60px', outline: 'none' }}
                    data-placeholder="Введите вопрос"
                  />
                </div>
              </div>
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ответ
                </label>
                
                <div
                  ref={(el) => {
                    if (el && !el.dataset.initialized) {
                      el.innerHTML = editingQuestion.answer;
                      el.dataset.initialized = 'true';
                    }
                  }}
                  contentEditable
                  onInput={(e) => {
                    setEditingQuestion({ ...editingQuestion, answer: e.target.innerHTML });
                    setActiveField('answer');
                  }}
                  onFocus={() => setActiveField('answer')}
                  suppressContentEditableWarning={true}
                  className="w-full p-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 min-h-[120px]"
                  style={{ 
                    minHeight: '120px', 
                    outline: 'none',
                    listStyleType: 'disc'
                  }}
                  data-placeholder="Введите ответ"
                />
              </div>
              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditQuestionModal(false);
                    setSelectedCategoryIndex(null);
                    setSelectedQuestionIndex(null);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Отмена
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
                >
                  Сохранить
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Модальное окно настроек темы */}
      {showThemeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-2xl font-bold mb-6">Настройки темы FAQ</h2>
              
              {/* Секция цветов */}
              <div className="mb-8">
                <h3 className="text-lg font-semibold mb-4 text-gray-800">Цвета</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Цвет фона категорий */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Цвет фона категорий
                    </label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="color"
                        value={themeSettings.categoryBgColor}
                        onChange={(e) => setThemeSettings(prev => ({ ...prev, categoryBgColor: e.target.value }))}
                        className="w-12 h-8 border border-gray-300 rounded cursor-pointer"
                      />
                      <input
                        type="text"
                        value={themeSettings.categoryBgColor}
                        onChange={(e) => setThemeSettings(prev => ({ ...prev, categoryBgColor: e.target.value }))}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                      />
                    </div>
                  </div>

                  {/* Цвет наведения категорий */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Цвет наведения категорий
                    </label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="color"
                        value={themeSettings.categoryHoverColor}
                        onChange={(e) => setThemeSettings(prev => ({ ...prev, categoryHoverColor: e.target.value }))}
                        className="w-12 h-8 border border-gray-300 rounded cursor-pointer"
                      />
                      <input
                        type="text"
                        value={themeSettings.categoryHoverColor}
                        onChange={(e) => setThemeSettings(prev => ({ ...prev, categoryHoverColor: e.target.value }))}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                      />
                    </div>
                  </div>

                  {/* Цвет фона вопросов */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Цвет фона вопросов
                    </label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="color"
                        value={themeSettings.questionBgColor}
                        onChange={(e) => setThemeSettings(prev => ({ ...prev, questionBgColor: e.target.value }))}
                        className="w-12 h-8 border border-gray-300 rounded cursor-pointer"
                      />
                      <input
                        type="text"
                        value={themeSettings.questionBgColor}
                        onChange={(e) => setThemeSettings(prev => ({ ...prev, questionBgColor: e.target.value }))}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                      />
                    </div>
                  </div>

                  {/* Цвет фона ответов */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Цвет фона ответов
                    </label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="color"
                        value={themeSettings.answerBgColor}
                        onChange={(e) => setThemeSettings(prev => ({ ...prev, answerBgColor: e.target.value }))}
                        className="w-12 h-8 border border-gray-300 rounded cursor-pointer"
                      />
                      <input
                        type="text"
                        value={themeSettings.answerBgColor}
                        onChange={(e) => setThemeSettings(prev => ({ ...prev, answerBgColor: e.target.value }))}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                      />
                    </div>
                  </div>

                  {/* Цвет рамки */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Цвет рамки
                    </label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="color"
                        value={themeSettings.borderColor}
                        onChange={(e) => setThemeSettings(prev => ({ ...prev, borderColor: e.target.value }))}
                        className="w-12 h-8 border border-gray-300 rounded cursor-pointer"
                      />
                      <input
                        type="text"
                        value={themeSettings.borderColor}
                        onChange={(e) => setThemeSettings(prev => ({ ...prev, borderColor: e.target.value }))}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                      />
                    </div>
                  </div>

                  {/* Цвет рамки контента */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Цвет рамки контента
                    </label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="color"
                        value={themeSettings.contentBorderColor}
                        onChange={(e) => setThemeSettings(prev => ({ ...prev, contentBorderColor: e.target.value }))}
                        className="w-12 h-8 border border-gray-300 rounded cursor-pointer"
                      />
                      <input
                        type="text"
                        value={themeSettings.contentBorderColor}
                        onChange={(e) => setThemeSettings(prev => ({ ...prev, contentBorderColor: e.target.value }))}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                      />
                    </div>
                  </div>

                  {/* Основной цвет */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Основной цвет
                    </label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="color"
                        value={themeSettings.primaryColor}
                        onChange={(e) => setThemeSettings(prev => ({ ...prev, primaryColor: e.target.value }))}
                        className="w-12 h-8 border border-gray-300 rounded cursor-pointer"
                      />
                      <input
                        type="text"
                        value={themeSettings.primaryColor}
                        onChange={(e) => setThemeSettings(prev => ({ ...prev, primaryColor: e.target.value }))}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                      />
                    </div>
                  </div>

                  {/* Цвет стрелочек */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Цвет стрелочек
                    </label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="color"
                        value={themeSettings.arrowColor}
                        onChange={(e) => setThemeSettings(prev => ({ ...prev, arrowColor: e.target.value }))}
                        className="w-12 h-8 border border-gray-300 rounded cursor-pointer"
                      />
                      <input
                        type="text"
                        value={themeSettings.arrowColor}
                        onChange={(e) => setThemeSettings(prev => ({ ...prev, arrowColor: e.target.value }))}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                      />
                    </div>
                  </div>

                  {/* Общий цвет темы */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Общий цвет темы
                    </label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="color"
                        value={themeSettings.generalColor}
                        onChange={(e) => setThemeSettings(prev => ({ ...prev, generalColor: e.target.value }))}
                        className="w-12 h-8 border border-gray-300 rounded cursor-pointer"
                      />
                      <input
                        type="text"
                        value={themeSettings.generalColor}
                        onChange={(e) => setThemeSettings(prev => ({ ...prev, generalColor: e.target.value }))}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Секция толщин рамок */}
              <div>
                <h3 className="text-lg font-semibold mb-4 text-gray-800">Толщины рамок</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Толщина рамки */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Толщина рамки категорий
                    </label>
                    <select
                      value={themeSettings.borderWidth}
                      onChange={(e) => setThemeSettings(prev => ({ ...prev, borderWidth: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                    >
                      <option value="1px">Тонкая (1px)</option>
                      <option value="2px">Средняя (2px)</option>
                      <option value="3px">Толстая (3px)</option>
                      <option value="4px">Очень толстая (4px)</option>
                    </select>
                  </div>

                  {/* Толщина рамки контента */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Толщина рамки контента
                    </label>
                    <select
                      value={themeSettings.contentBorderWidth}
                      onChange={(e) => setThemeSettings(prev => ({ ...prev, contentBorderWidth: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                    >
                      <option value="0px">Без рамки</option>
                      <option value="1px">Тонкая (1px)</option>
                      <option value="2px">Средняя (2px)</option>
                      <option value="3px">Толстая (3px)</option>
                      <option value="4px">Очень толстая (4px)</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Предпросмотр темы */}
              <div className="mt-6 p-4 border border-gray-200 rounded-lg bg-gray-50">
                <h3 className="text-lg font-medium mb-3">Предпросмотр темы:</h3>
                <div className="space-y-2">
                  <div 
                    className="p-3 rounded border"
                    style={{ 
                      backgroundColor: themeSettings.categoryBgColor,
                      borderColor: themeSettings.borderColor,
                      borderWidth: themeSettings.borderWidth
                    }}
                  >
                    <div 
                      className="font-medium"
                      style={{ color: themeSettings.primaryColor }}
                    >
                      Категория
                    </div>
                  </div>
                  <div className="ml-4 space-y-1">
                    <div 
                      className="p-2 rounded"
                      style={{ backgroundColor: themeSettings.questionBgColor }}
                    >
                      <div className="font-medium text-sm">Вопрос</div>
                    </div>
                    <div 
                      className="p-2 rounded"
                      style={{ backgroundColor: themeSettings.answerBgColor }}
                    >
                      <div className="text-sm">Ответ</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Кнопки */}
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowThemeModal(false)}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Отмена
                </button>
                <button
                  onClick={saveTheme}
                  className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors"
                >
                  Сохранить тему
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FAQEditor;
